import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './AdminDashboard.css';
import griffsoLogo from '../assets/griffso.png';

const API_BASE_URL = 'https://ai.griffso.com/api/parsing';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSectionRaw] = useState(() => {
    const hash = window.location.hash.replace('#', '');
    const valid = ['overview','tenants','payments','data','api','documents','analytics','logs','security','system','plans','administration'];
    return valid.includes(hash) ? hash : 'overview';
  });
  const adminRole = sessionStorage.getItem('admin_role') || 'support';
  const adminPerms = (() => { try { return JSON.parse(sessionStorage.getItem('admin_permissions') || '{}'); } catch { return {}; } })();
  const setActiveSection = (s) => {
    setActiveSectionRaw(s);
    window.location.hash = s;
  };
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [analyticsData, setAnalyticsData] = useState(null);
  const [logsData, setLogsData] = useState(null);
  const [logContent, setLogContent] = useState(null);
  const [activeLog, setActiveLog] = useState(null);
  const [tenants, setTenants] = useState({});
  const [allFeatures, setAllFeatures] = useState([]);
  const [planDefs, setPlanDefs] = useState({});
  const [tenantModal, setTenantModal] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [viewClient, setViewClient] = useState(null);
  const [clientActivity, setClientActivity] = useState(null);
  const [clientDetailTab, setClientDetailTab] = useState('overview');
  const [clientTxns, setClientTxns] = useState(null);
  const [txnModal, setTxnModal] = useState(null);
  const [modalTab, setModalTab] = useState('general');
  const [dataSearch, setDataSearch] = useState('');
  const [dataSortKey, setDataSortKey] = useState('name');
  const [dataSortAsc, setDataSortAsc] = useState(true);
  const [dataPage, setDataPage] = useState(1);
  const [dataPerPage, setDataPerPage] = useState(20);
  const [notifyStatus, setNotifyStatus] = useState({});
  const [emailModal, setEmailModal] = useState(null);
  const [apiStats, setApiStats] = useState(null);
  const [plans, setPlans] = useState([]);
  const [planModal, setPlanModal] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [adminModal, setAdminModal] = useState(null);
  const [viewPw, setViewPw] = useState(null);
  const [defaultPerms, setDefaultPerms] = useState({});
  const [tester, setTester] = useState({
    method: 'POST', url: `https://ai.griffso.com/api/parsing/auth`,
    headers: [{ key: 'Content-Type', value: 'application/json', enabled: true }],
    body: JSON.stringify({ Authorization: '', client_name: '' }, null, 2),
    bodyType: 'json',
    formFields: [{ key: '', value: '', type: 'text', file: null }],
    sending: false, response: null,
  });

  useEffect(() => {
    const token = sessionStorage.getItem('admin_token');
    if (!token) { navigate('/admin', { replace: true }); return; }
    fetchHealth();
    fetchAnalytics();
    fetchLogs();
    fetchTenants();
    fetchApiStats();
    fetchPlans();
    fetchAdmins();
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, [navigate]);

  const fetchHealth = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/health`);
      const data = await res.json();
      setHealthData(data);
    } catch { /* silent */ }
    setLoading(false);
  };

  const fetchAnalytics = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/admin/analytics`);
      const data = await res.json();
      setAnalyticsData(data);
    } catch { /* silent */ }
  };

  const fetchLogs = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/admin/logs`);
      const data = await res.json();
      setLogsData(data);
    } catch { /* silent */ }
  };

  const fetchLogContent = async (type, filename) => {
    setActiveLog({ type, filename });
    try {
      const res = await fetch(`${API_BASE_URL}/admin/logs/${type}/${filename}`);
      const data = await res.json();
      setLogContent(data.content || 'No content');
    } catch { setLogContent('Failed to load log'); }
  };

  const fetchApiStats = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/admin/api-stats`);
      const data = await res.json();
      setApiStats(data);
    } catch { /* silent */ }
  };

  const fetchPlans = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/admin/plans`);
      const data = await res.json();
      setPlans(data.plans || []);
    } catch { /* silent */ }
  };

  const savePlan = async () => {
    if (!planModal) return;
    try {
      if (planModal.mode === 'add') {
        await fetch(`${API_BASE_URL}/admin/plans`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(planModal.data),
        });
      } else {
        await fetch(`${API_BASE_URL}/admin/plans/${planModal.id}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(planModal.data),
        });
      }
      setPlanModal(null);
      fetchPlans();
      fetchTenants();
    } catch { /* silent */ }
  };

  const deletePlan = async (planId) => {
    try {
      const res = await fetch(`${API_BASE_URL}/admin/plans/${planId}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.error) { alert(data.error.msg); return; }
      fetchPlans();
    } catch { /* silent */ }
  };

  const sendTestRequest = async () => {
    setTester(p => ({ ...p, sending: true, response: null }));
    const start = performance.now();
    try {
      const hdrs = {};
      tester.headers.filter(h => h.enabled && h.key).forEach(h => { hdrs[h.key] = h.value; });
      const opts = { method: tester.method, headers: hdrs };
      if (['POST', 'PUT', 'PATCH'].includes(tester.method)) {
        if (tester.bodyType === 'form-data') {
          const fd = new FormData();
          tester.formFields.filter(f => f.key).forEach(f => {
            if (f.type === 'file' && f.file) fd.append(f.key, f.file, f.file.name);
            else if (f.type === 'text') fd.append(f.key, f.value || '');
          });
          opts.body = fd;
          delete opts.headers['Content-Type'];
        } else if (tester.body.trim()) {
          opts.body = tester.body;
        }
      }
      const res = await fetch(tester.url, opts);
      const elapsed = Math.round(performance.now() - start);
      const ct = res.headers.get('content-type') || '';
      let body;
      if (ct.includes('json')) {
        body = JSON.stringify(await res.json(), null, 2);
      } else {
        body = await res.text();
      }
      const respHeaders = {};
      res.headers.forEach((v, k) => { respHeaders[k] = v; });
      // Auto-capture token from /auth response
      let lastToken = tester._lastToken;
      if (tester.url.endsWith('/auth') && res.status === 200) {
        try {
          const parsed = JSON.parse(body);
          if (parsed.access_token) lastToken = parsed.access_token;
        } catch {}
      }
      setTester(p => ({ ...p, sending: false, _lastToken: lastToken, response: {
        status: res.status, statusText: res.statusText, time: elapsed,
        size: new Blob([body]).size, body, headers: respHeaders,
      }}));
    } catch (err) {
      const elapsed = Math.round(performance.now() - start);
      setTester(p => ({ ...p, sending: false, response: {
        status: 0, statusText: 'Network Error', time: elapsed,
        size: 0, body: err.message, headers: {},
      }}));
    }
  };

  const updateFormField = (idx, field, val) => {
    setTester(p => {
      const ff = [...p.formFields];
      ff[idx] = { ...ff[idx], [field]: val };
      if (field === 'type' && val === 'file') ff[idx].value = '';
      if (field === 'type' && val === 'text') ff[idx].file = null;
      return { ...p, formFields: ff };
    });
  };

  const addFormField = () => {
    setTester(p => ({ ...p, formFields: [...p.formFields, { key: '', value: '', type: 'text', file: null }] }));
  };

  const removeFormField = (idx) => {
    setTester(p => ({ ...p, formFields: p.formFields.filter((_, i) => i !== idx) }));
  };

  const updateTesterHeader = (idx, field, val) => {
    setTester(p => {
      const h = [...p.headers];
      h[idx] = { ...h[idx], [field]: val };
      return { ...p, headers: h };
    });
  };

  const addTesterHeader = () => {
    setTester(p => ({ ...p, headers: [...p.headers, { key: '', value: '', enabled: true }] }));
  };

  const removeTesterHeader = (idx) => {
    setTester(p => ({ ...p, headers: p.headers.filter((_, i) => i !== idx) }));
  };

  const testerPresets = [
    { label: 'Auth', method: 'POST', url: `${API_BASE_URL}/auth`,
      headers: [{ key: 'Content-Type', value: 'application/json', enabled: true }],
      body: JSON.stringify({ Authorization: 'sk_client1_xxxx', client_name: 'client1' }, null, 2) },
    { label: 'Refresh', method: 'POST', url: `${API_BASE_URL}/refresh`,
      headers: [{ key: 'Content-Type', value: 'application/json', enabled: true }],
      body: JSON.stringify({ refresh_token: '<paste_token_here>' }, null, 2) },
    { label: 'Process (ID)', method: 'POST', url: `${API_BASE_URL}/process`,
      headers: [{ key: 'Authorization', value: 'Bearer <access_token>', enabled: true }],
      bodyType: 'form-data',
      formFields: [
        { key: 'file', value: '', type: 'file', file: null },
        { key: 'document_type', value: 'PAN', type: 'text', file: null },
      ],
      body: '' },
    { label: 'Process (PDF)', method: 'POST', url: `${API_BASE_URL}/process`,
      headers: [{ key: 'Authorization', value: 'Bearer <access_token>', enabled: true }],
      bodyType: 'form-data',
      formFields: [
        { key: 'file', value: '', type: 'file', file: null },
        { key: 'document_type', value: 'PDF', type: 'text', file: null },
        { key: 'pdf_category', value: 'RESUME', type: 'text', file: null },
      ],
      body: '' },
    { label: 'Process (Bank Stmt)', method: 'POST', url: `${API_BASE_URL}/process`,
      headers: [{ key: 'Authorization', value: 'Bearer <access_token>', enabled: true }],
      bodyType: 'form-data',
      formFields: [
        { key: 'file', value: '', type: 'file', file: null },
        { key: 'document_type', value: 'PDF', type: 'text', file: null },
        { key: 'pdf_category', value: 'BANK_STATEMENT', type: 'text', file: null },
      ],
      body: '' },
    { label: 'Process (Aadhaar)', method: 'POST', url: `${API_BASE_URL}/process`,
      headers: [{ key: 'Authorization', value: 'Bearer <access_token>', enabled: true }],
      bodyType: 'form-data',
      formFields: [
        { key: 'front', value: '', type: 'file', file: null },
        { key: 'back', value: '', type: 'file', file: null },
        { key: 'document_type', value: 'AADHAAR', type: 'text', file: null },
      ],
      body: '' },
    { label: 'Health', method: 'GET', url: `${API_BASE_URL}/health`,
      headers: [], body: '' },
    { label: 'Tenants', method: 'GET', url: `${API_BASE_URL}/admin/tenants`,
      headers: [], body: '' },
    { label: 'Analytics', method: 'GET', url: `${API_BASE_URL}/admin/analytics`,
      headers: [], body: '' },
  ];

  const [testerTab, setTesterTab] = useState('body');
  const [respTab, setRespTab] = useState('body');

  const fetchClientActivity = async (clientId) => {
    setClientActivity(null);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/tenants/${clientId}/activity`);
      const data = await res.json();
      setClientActivity(data);
    } catch { /* silent */ }
  };

  const openClientView = (clientId) => {
    setViewClient(clientId);
    setClientDetailTab('overview');
    fetchClientActivity(clientId);
    fetchClientTxns(clientId);
  };

  const fetchClientTxns = async (clientId) => {
    setClientTxns(null);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/tenants/${clientId}/transactions`);
      const data = await res.json();
      setClientTxns(data);
    } catch { /* silent */ }
  };

  const saveTransaction = async () => {
    if (!txnModal || !viewClient) return;
    try {
      await fetch(`${API_BASE_URL}/admin/tenants/${viewClient}/transactions`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(txnModal),
      });
      setTxnModal(null);
      fetchClientTxns(viewClient);
    } catch { /* silent */ }
  };

  const deleteTransaction = async (txnId) => {
    if (!viewClient) return;
    try {
      await fetch(`${API_BASE_URL}/admin/tenants/${viewClient}/transactions/${txnId}`, { method: 'DELETE' });
      fetchClientTxns(viewClient);
    } catch { /* silent */ }
  };

  const fetchTenants = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/admin/tenants`);
      const data = await res.json();
      setTenants(data.tenants || {});
      setAllFeatures(data.all_features || []);
      setPlanDefs(data.plan_definitions || {});
    } catch { /* silent */ }
  };

  const formatPrice = (p) => p === 0 ? 'Free' : `₹${(p / 100).toLocaleString()}`;
  const formatRupee = (p) => p === 0 || p == null ? 'Free' : `₹${Number(p).toLocaleString('en-IN')}`;

  const saveTenant = async () => {
    if (!tenantModal) return;
    const { mode, id, data } = tenantModal;
    try {
      if (mode === 'add') {
        await fetch(`${API_BASE_URL}/admin/tenants`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ client_id: data.client_id, ...data }),
        });
      } else {
        await fetch(`${API_BASE_URL}/admin/tenants/${id}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        });
      }
      setTenantModal(null);
      fetchTenants();
    } catch { /* silent */ }
  };

  const deleteTenant = async (clientId) => {
    try {
      await fetch(`${API_BASE_URL}/admin/tenants/${clientId}`, { method: 'DELETE' });
      setDeleteConfirm(null);
      fetchTenants();
    } catch { /* silent */ }
  };

  const emptyTenant = {
    client_id: '', key: '', name: '', features: [], active: true,
    plan: 'free', billing_cycle: 'monthly', plan_start: '', plan_end: '',
    api_limit: 100, api_used: 0, payment_status: 'trial',
    last_payment_date: '', total_revenue: 0,
    contact_email: '', contact_phone: '', company_address: '', notes: '',
    login_username: '', login_password: '',
    summary_config: { include_key_points: true, include_full_text: true },
    api_integration_enabled: true,
  };

  const openAddModal = () => {
    setModalTab('general');
    setTenantModal({ mode: 'add', id: null, data: { ...emptyTenant } });
  };

  const openEditModal = (clientId) => {
    const t = tenants[clientId];
    setModalTab('general');
    setTenantModal({
      mode: 'edit', id: clientId,
      data: {
        key: t.key || '', name: t.name || '', features: [...(t.features || [])], active: t.active !== false,
        plan: t.plan || 'free', billing_cycle: t.billing_cycle || 'monthly',
        plan_start: t.plan_start || '', plan_end: t.plan_end || '',
        api_limit: t.api_limit ?? 100, api_used: t.api_used ?? 0,
        payment_status: t.payment_status || 'trial',
        last_payment_date: t.last_payment_date || '', total_revenue: t.total_revenue ?? 0,
        contact_email: t.contact_email || '', contact_phone: t.contact_phone || '',
        company_address: t.company_address || '', notes: t.notes || '',
        login_username: t.login_username || '', login_password: t.login_password || '',
        summary_config: { ...(t.summary_config || { include_key_points: true, include_full_text: true }) },
        api_integration_enabled: t.api_integration_enabled !== false,
      }
    });
  };

  const updateModalField = (field, value) => {
    setTenantModal(prev => ({ ...prev, data: { ...prev.data, [field]: value } }));
  };

  const toggleModalFeature = (feat) => {
    setTenantModal(prev => {
      const feats = prev.data.features.includes(feat)
        ? prev.data.features.filter(f => f !== feat)
        : [...prev.data.features, feat];
      return { ...prev, data: { ...prev.data, features: feats } };
    });
  };

  const handleLogDownload = (type, filename) => {
    window.open(`${API_BASE_URL}/admin/logs/${type}/${filename}?download=true`, '_blank');
  };

  const fetchAdmins = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/admin/admins`);
      const data = await res.json();
      setAdmins(data.admins || []);
      setDefaultPerms(data.default_permissions || {});
    } catch { /* silent */ }
  };

  const saveAdmin = async () => {
    if (!adminModal) return;
    try {
      if (adminModal.mode === 'add') {
        await fetch(`${API_BASE_URL}/admin/admins`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(adminModal.data),
        });
      } else {
        await fetch(`${API_BASE_URL}/admin/admins/${adminModal.id}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(adminModal.data),
        });
      }
      setAdminModal(null);
      fetchAdmins();
    } catch { /* silent */ }
  };

  const deleteAdmin = async (id) => {
    try {
      const res = await fetch(`${API_BASE_URL}/admin/admins/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.error) { alert(data.error.msg); return; }
      fetchAdmins();
    } catch { /* silent */ }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin_token');
    sessionStorage.removeItem('admin_user');
    sessionStorage.removeItem('admin_role');
    sessionStorage.removeItem('admin_permissions');
    sessionStorage.removeItem('admin_id');
    navigate('/admin');
  };

  const adminUser = sessionStorage.getItem('admin_user') || 'Admin';

  const allNavItems = [
    { id: 'overview', icon: '📊', label: 'Overview' },
    { id: 'tenants', icon: '🏢', label: 'Tenants' },
    { id: 'payments', icon: '💳', label: 'Payments' },
    { id: 'plans', icon: '💎', label: 'Pricing & Plans' },
    { id: 'data', icon: '🗂️', label: 'Data & Reports' },
    { id: 'api', icon: '🌐', label: 'API Monitor' },
    { id: 'documents', icon: '📄', label: 'Documents' },
    { id: 'analytics', icon: '📈', label: 'Analytics' },
    { id: 'logs', icon: '📋', label: 'Logs & Monitoring' },
    { id: 'security', icon: '🔒', label: 'Security' },
    { id: 'administration', icon: '👥', label: 'Administration' },
    { id: 'system', icon: '⚙️', label: 'System' },
  ];
  const navItems = allNavItems.filter(n => n.id === 'overview' || adminPerms[n.id] !== false);

  const stats = [
    { label: 'Total Tenants', value: healthData?.security_intelligence?.multi_tenant_architecture?.total_tenants || 0, icon: '🏢', color: '#6366f1', trend: '+2 this month' },
    { label: 'Active Tenants', value: healthData?.security_intelligence?.multi_tenant_architecture?.active_tenants || 0, icon: '✅', color: '#22c55e', trend: 'All active' },
    { label: 'API Uptime', value: healthData?.status === 'healthy' ? '99.9%' : 'Down', icon: '⚡', color: '#f59e0b', trend: 'Last 30 days' },
    { label: 'Doc Types', value: '9', icon: '📋', color: '#ec4899', trend: 'PAN, Aadhaar, DL, Voter, Resume, Notice, Summary, Bank Stmt, Other' },
  ];

  const renderOverview = () => (
    <div className="dash-section">
      <div className="stats-grid">
        {stats.map((s, i) => (
          <div key={i} className="stat-glass-card" style={{ '--accent': s.color }}>
            <div className="stat-card-glow" />
            <div className="stat-icon-wrap">
              <span className="stat-icon">{s.icon}</span>
            </div>
            <div className="stat-info">
              <span className="stat-value">{s.value}</span>
              <span className="stat-label">{s.label}</span>
              <span className="stat-trend">{s.trend}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="dash-grid-2">
        <div className="glass-panel">
          <div className="panel-head">
            <span className="panel-icon">🧠</span>
            <h3>AI Engine Status</h3>
          </div>
          <div className="engine-list">
            {healthData?.ai_model_intelligence?.ai_engines && Object.entries(healthData.ai_model_intelligence.ai_engines).map(([key, eng]) => (
              <div key={key} className="engine-row">
                <div className="engine-info">
                  <span className="engine-name">{eng.name}</span>
                  <span className="engine-type">{eng.type}</span>
                </div>
                <div className="engine-badge">{eng.accuracy_range || eng.accuracy || eng.speed_advantage}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="glass-panel">
          <div className="panel-head">
            <span className="panel-icon">📈</span>
            <h3>Performance</h3>
          </div>
          <div className="perf-metrics">
            {healthData?.performance_intelligence?.processing_metrics && Object.entries(healthData.performance_intelligence.processing_metrics).map(([key, val]) => (
              <div key={key} className="perf-row">
                <span className="perf-label">{key.replace(/_/g, ' ')}</span>
                <span className="perf-value">{val}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="glass-panel">
        <div className="panel-head">
          <span className="panel-icon">🎯</span>
          <h3>Accuracy Benchmarks</h3>
        </div>
        <div className="benchmark-grid">
          {healthData?.performance_intelligence?.accuracy_benchmarks && Object.entries(healthData.performance_intelligence.accuracy_benchmarks).map(([key, val]) => (
            <div key={key} className="benchmark-item">
              <span className="bench-label">{key.replace(/_/g, ' ')}</span>
              <div className="bench-bar-track">
                <div className="bench-bar-fill" style={{ width: typeof val === 'string' && val.includes('-') ? val.split('-')[1] : val }} />
              </div>
              <span className="bench-value">{val}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderTenants = () => {
    const tList = Object.entries(tenants);
    const totalRevenue = tList.reduce((s, [, t]) => s + (t.total_revenue || 0), 0);
    const paidCount = tList.filter(([, t]) => t.payment_status === 'paid').length;
    const overdueCount = tList.filter(([, t]) => t.payment_status === 'overdue').length;

    return (
      <div className="dash-section">
        {viewClient && tenants[viewClient] ? null : <>
        {/* Revenue Stats */}
        <div className="stats-grid">
          {[
            { icon: '🏢', label: 'Total Tenants', value: tList.length, color: '#6366f1' },
            { icon: '💰', label: 'Total Revenue', value: formatPrice(totalRevenue), color: '#22c55e' },
            { icon: '✅', label: 'Paid Clients', value: paidCount, color: '#f59e0b' },
            { icon: '⚠️', label: 'Overdue', value: overdueCount, color: '#ef4444' },
          ].map((s, i) => (
            <div key={i} className="stat-glass-card" style={{ '--accent': s.color }}>
              <div className="stat-card-glow" />
              <div className="stat-icon-wrap"><span className="stat-icon">{s.icon}</span></div>
              <div className="stat-info">
                <span className="stat-value">{s.value}</span>
                <span className="stat-label">{s.label}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="glass-panel">
          <div className="panel-head">
            <span className="panel-icon">🏢</span>
            <h3>Tenant Management</h3>
            <button className="export-btn excel" style={{ marginRight: 8 }} onClick={handleExcelDownload}>📥 Excel</button>
            <button className="tenant-add-btn" onClick={openAddModal}>+ Add Tenant</button>
          </div>
          <div className="table-wrap">
            <table className="dash-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Plan</th>
                  <th>Billing</th>
                  <th>API Usage</th>
                  <th>Payment</th>
                  <th>Revenue</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tList.map(([id, t]) => (
                  <tr key={id}>
                    <td>
                      <div className="tenant-cell">
                        <span className="tenant-name clickable" onClick={() => openClientView(id)}>{t.name}</span>
                        <span className="tenant-id">{id}</span>
                      </div>
                    </td>
                    <td><span className={`plan-badge ${t.plan || 'free'}`}>{(t.plan || 'free').toUpperCase()}</span></td>
                    <td>
                      <div className="billing-cell">
                        <span>{t.billing_cycle === 'yearly' ? '📅 Yearly' : '📅 Monthly'}</span>
                        <span className="text-muted">{t.plan_start || '—'}</span>
                      </div>
                    </td>
                    <td>
                      <div className="api-usage-cell">
                        <div className="usage-bar-track">
                          <div className="usage-bar-fill" style={{ width: `${t.api_limit === -1 ? 30 : Math.min(100, ((t.api_used || 0) / (t.api_limit || 1)) * 100)}%` }} />
                        </div>
                        <span className="usage-text">{(t.api_used || 0).toLocaleString()} / {t.api_limit === -1 ? '∞' : (t.api_limit || 0).toLocaleString()}</span>
                      </div>
                    </td>
                    <td><span className={`pay-badge ${t.payment_status || 'trial'}`}>{(t.payment_status || 'trial').toUpperCase()}</span></td>
                    <td><span className="revenue-text">{formatPrice(t.total_revenue || 0)}</span></td>
                    <td><span className={`status-dot ${t.active ? 'active' : 'inactive'}`}>{t.active ? 'Active' : 'Inactive'}</span></td>
                    <td>
                      <div className="tenant-actions">
                        <button className="t-action-btn edit" onClick={() => openEditModal(id)}>✏️</button>
                        <button className="t-action-btn delete" onClick={() => setDeleteConfirm(id)}>🗑️</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Tenant Modal - Tabbed */}
        {tenantModal && (
          <div className="modal-overlay" onClick={() => setTenantModal(null)}>
            <div className="modal-glass wide" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>{tenantModal.mode === 'add' ? '➕ Add New Tenant' : `✏️ Edit: ${tenantModal.id}`}</h3>
                <button className="modal-close" onClick={() => setTenantModal(null)}>✕</button>
              </div>
              {/* Tabs */}
              <div className="modal-tabs">
                {[['general','🏢 General'],['billing','💳 Billing & Plan'],['contact','📞 Contact'],['login','🔑 User Login'],['config','⚙️ Config']].map(([key, label]) => (
                  <button key={key} className={`modal-tab ${modalTab === key ? 'active' : ''}`} onClick={() => setModalTab(key)}>{label}</button>
                ))}
              </div>
              <div className="modal-body">
                {/* General Tab */}
                {modalTab === 'general' && (<>
                  {tenantModal.mode === 'add' && (
                    <div className="modal-field">
                      <label>Client ID</label>
                      <input type="text" value={tenantModal.data.client_id || ''} onChange={e => updateModalField('client_id', e.target.value)} placeholder="e.g. client5" />
                    </div>
                  )}
                  <div className="modal-field">
                    <label>Tenant Name</label>
                    <input type="text" value={tenantModal.data.name} onChange={e => updateModalField('name', e.target.value)} placeholder="Company Name" />
                  </div>
                  <div className="modal-field">
                    <label>API Key</label>
                    <input type="text" value={tenantModal.data.key} onChange={e => updateModalField('key', e.target.value)} placeholder="sk_clientX_..." />
                  </div>
                  <div className="modal-field">
                    <label>Features</label>
                    <div className="feature-checkboxes">
                      {allFeatures.map(f => (
                        <label key={f} className={`feat-check ${tenantModal.data.features.includes(f) ? 'checked' : ''}`}>
                          <input type="checkbox" checked={tenantModal.data.features.includes(f)} onChange={() => toggleModalFeature(f)} />
                          {f}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="modal-field">
                    <label>Status</label>
                    <div className="toggle-wrap" onClick={() => updateModalField('active', !tenantModal.data.active)}>
                      <div className={`toggle-track ${tenantModal.data.active ? 'on' : ''}`}><div className="toggle-thumb" /></div>
                      <span>{tenantModal.data.active ? 'Active' : 'Inactive'}</span>
                    </div>
                  </div>
                </>)}

                {/* Billing Tab */}
                {modalTab === 'billing' && (<>
                  <div className="modal-field">
                    <label>Plan</label>
                    <div className="plan-selector">
                      {Object.entries(planDefs).map(([key, p]) => (
                        <div key={key} className={`plan-option ${tenantModal.data.plan === key ? 'selected' : ''}`} onClick={() => {
                          updateModalField('plan', key);
                          updateModalField('api_limit', p.api_limit);
                        }}>
                          <span className="plan-option-name">{p.label}</span>
                          <span className="plan-option-price">{p.price_monthly === 0 || p.price_monthly == null ? 'Free' : `₹${Number(p.price_monthly).toLocaleString('en-IN')}/mo`}</span>
                          <span className="plan-option-limit">{p.api_limit === -1 ? 'Unlimited' : `${p.api_limit.toLocaleString()} calls/mo`}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="modal-row">
                    <div className="modal-field flex1">
                      <label>Billing Cycle</label>
                      <div className="cycle-toggle">
                        {['monthly', 'yearly'].map(c => (
                          <button key={c} className={`cycle-btn ${tenantModal.data.billing_cycle === c ? 'active' : ''}`}
                            onClick={() => updateModalField('billing_cycle', c)}>{c === 'monthly' ? '📅 Monthly' : '📅 Yearly'}</button>
                        ))}
                      </div>
                    </div>
                    <div className="modal-field flex1">
                      <label>Payment Status</label>
                      <select className="modal-select" value={tenantModal.data.payment_status} onChange={e => updateModalField('payment_status', e.target.value)}>
                        {['trial','paid','pending','overdue'].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="modal-row">
                    <div className="modal-field flex1">
                      <label>Plan Start Date</label>
                      <input type="date" value={tenantModal.data.plan_start} onChange={e => updateModalField('plan_start', e.target.value)} />
                    </div>
                    <div className="modal-field flex1">
                      <label>Plan End Date</label>
                      <input type="date" value={tenantModal.data.plan_end} onChange={e => updateModalField('plan_end', e.target.value)} />
                    </div>
                  </div>
                  <div className="modal-row">
                    <div className="modal-field flex1">
                      <label>API Limit (per month)</label>
                      <input type="number" value={tenantModal.data.api_limit} onChange={e => updateModalField('api_limit', parseInt(e.target.value) || 0)} />
                      <span className="field-hint">-1 = Unlimited</span>
                    </div>
                    <div className="modal-field flex1">
                      <label>API Used (this month)</label>
                      <input type="number" value={tenantModal.data.api_used} onChange={e => updateModalField('api_used', parseInt(e.target.value) || 0)} />
                    </div>
                  </div>
                  <div className="modal-row">
                    <div className="modal-field flex1">
                      <label>Last Payment Date</label>
                      <input type="date" value={tenantModal.data.last_payment_date} onChange={e => updateModalField('last_payment_date', e.target.value)} />
                    </div>
                    <div className="modal-field flex1">
                      <label>Total Revenue (paise)</label>
                      <input type="number" value={tenantModal.data.total_revenue} onChange={e => updateModalField('total_revenue', parseInt(e.target.value) || 0)} />
                      <span className="field-hint">{formatPrice(tenantModal.data.total_revenue || 0)}</span>
                    </div>
                  </div>
                </>)}

                {/* Contact Tab */}
                {modalTab === 'contact' && (<>
                  <div className="modal-field">
                    <label>Contact Email</label>
                    <input type="text" value={tenantModal.data.contact_email} onChange={e => updateModalField('contact_email', e.target.value)} placeholder="admin@company.com" />
                  </div>
                  <div className="modal-field">
                    <label>Contact Phone</label>
                    <input type="text" value={tenantModal.data.contact_phone} onChange={e => updateModalField('contact_phone', e.target.value)} placeholder="+91-9876543210" />
                  </div>
                  <div className="modal-field">
                    <label>Company Address</label>
                    <input type="text" value={tenantModal.data.company_address} onChange={e => updateModalField('company_address', e.target.value)} placeholder="City, State" />
                  </div>
                  <div className="modal-field">
                    <label>Admin Notes</label>
                    <textarea className="modal-textarea" value={tenantModal.data.notes} onChange={e => updateModalField('notes', e.target.value)} placeholder="Internal notes about this client..." rows={3} />
                  </div>
                </>)}

                {/* User Login Tab */}
                {modalTab === 'login' && (<>
                  <div className="modal-field">
                    <label>Username</label>
                    <input type="text" value={tenantModal.data.login_username} onChange={e => updateModalField('login_username', e.target.value)} placeholder="e.g. client_admin" />
                  </div>
                  <div className="modal-field">
                    <label>Password</label>
                    <div className="password-field-wrap">
                      <input type="text" value={tenantModal.data.login_password} onChange={e => updateModalField('login_password', e.target.value)} placeholder="Set password" />
                    </div>
                  </div>
                  <div className="modal-field">
                    <button className="reset-pwd-btn" onClick={() => {
                      const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789@#$&!';
                      let pwd = '';
                      for (let i = 0; i < 12; i++) pwd += chars[Math.floor(Math.random() * chars.length)];
                      updateModalField('login_password', pwd);
                    }}>🔄 Reset / Generate Password</button>
                    <span className="field-hint">Generates a random 12-character secure password</span>
                  </div>
                </>)}

                {/* Config Tab */}
                {modalTab === 'config' && (<>
                  <div className="modal-field">
                    <label>API Integration</label>
                    <div className="toggle-wrap" onClick={() => updateModalField('api_integration_enabled', !tenantModal.data.api_integration_enabled)}>
                      <div className={`toggle-track ${tenantModal.data.api_integration_enabled ? 'on' : ''}`}><div className="toggle-thumb" /></div>
                      <span>{tenantModal.data.api_integration_enabled ? '✅ Enabled — Client can use API externally' : '❌ Disabled — API access blocked'}</span>
                    </div>
                  </div>
                  <div className="modal-field">
                    <label>Summary Config</label>
                    <div className="summary-toggles">
                      <label className="feat-check small">
                        <input type="checkbox" checked={tenantModal.data.summary_config?.include_key_points || false}
                          onChange={e => updateModalField('summary_config', { ...tenantModal.data.summary_config, include_key_points: e.target.checked })} />
                        Include Key Points
                      </label>
                      <label className="feat-check small">
                        <input type="checkbox" checked={tenantModal.data.summary_config?.include_full_text || false}
                          onChange={e => updateModalField('summary_config', { ...tenantModal.data.summary_config, include_full_text: e.target.checked })} />
                        Include Full Text
                      </label>
                    </div>
                  </div>
                </>)}
              </div>
              <div className="modal-footer">
                <button className="modal-btn cancel" onClick={() => setTenantModal(null)}>Cancel</button>
                <button className="modal-btn save" onClick={saveTenant}>{tenantModal.mode === 'add' ? 'Create Tenant' : 'Save Changes'}</button>
              </div>
            </div>
          </div>
        )}

        </>
        }

        {/* Delete Confirmation */}
        {deleteConfirm && (
          <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
            <div className="modal-glass small" onClick={e => e.stopPropagation()}>
              <div className="modal-header"><h3>⚠️ Delete Tenant</h3></div>
              <div className="modal-body">
                <p className="delete-msg">Are you sure you want to delete <strong>{deleteConfirm}</strong>? This action cannot be undone.</p>
              </div>
              <div className="modal-footer">
                <button className="modal-btn cancel" onClick={() => setDeleteConfirm(null)}>Cancel</button>
                <button className="modal-btn danger" onClick={() => deleteTenant(deleteConfirm)}>Delete</button>
              </div>
            </div>
          </div>
        )}

        {/* Client Detail View - Full Screen */}
        {viewClient && tenants[viewClient] && (() => {
          const t = tenants[viewClient];
          const a = clientActivity;
          const s = a?.summary || {};
          const pDef = planDefs[t.plan] || {};
          const usagePct = t.api_limit === -1 ? 0 : Math.min(100, ((t.api_used || 0) / (t.api_limit || 1)) * 100);
          const statusColor = (sc) => sc < 300 ? '#22c55e' : sc < 400 ? '#f59e0b' : '#ef4444';
          const tabs = [['overview','📊 Overview'],['api','📡 API Logs'],['docs','📄 Documents'],['notifications','🔔 Notifications'],['billing','💳 Billing'],['config','⚙️ Config']];

          return (
            <div className="client-detail-screen">
                {/* Header */}
                <div className="client-detail-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <button className="client-back-btn" onClick={() => setViewClient(null)}>← Back</button>
                    <span style={{ fontSize: '1.5rem' }}>🏢</span>
                    <div>
                      <h3 style={{ margin: 0 }}>{t.name}</h3>
                      <span className="tenant-id" style={{ fontSize: '.78rem' }}>{viewClient}</span>
                    </div>
                    <span className={`status-dot ${t.active ? 'active' : 'inactive'}`} style={{ marginLeft: 8 }}>{t.active ? 'Active' : 'Inactive'}</span>
                    <span className={`plan-badge ${t.plan || 'free'}`} style={{ marginLeft: 4 }}>{(t.plan || 'free').toUpperCase()}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="t-action-btn edit" onClick={() => { const cid = viewClient; setViewClient(null); openEditModal(cid); }}>✏️</button>
                    <button className="export-btn excel" onClick={() => {
                      const cid = viewClient;
                      window.open(`${API_BASE_URL}/admin/tenants/${cid}/export`, '_blank');
                    }}>📥 Excel</button>
                  </div>
                </div>

                {/* Tabs */}
                <div className="client-detail-tabs">
                  {tabs.map(([k, l]) => (
                    <button key={k} className={`modal-tab ${clientDetailTab === k ? 'active' : ''}`} onClick={() => setClientDetailTab(k)}>{l}</button>
                  ))}
                </div>

                <div className="client-detail-body">
                  {/* Overview Tab */}
                  {clientDetailTab === 'overview' && (<>
                    <div className="stats-grid" style={{ marginBottom: 20 }}>
                      {[
                        { icon: '📡', label: 'Total API Calls', value: s.total_api_calls || 0, color: '#6366f1' },
                        { icon: '📄', label: 'Docs Processed', value: s.total_docs || 0, color: '#22c55e' },
                        { icon: '❌', label: 'Errors', value: s.total_errors || 0, color: '#ef4444' },
                        { icon: '⚡', label: 'Avg Response', value: `${s.avg_response_ms || 0}ms`, color: '#f59e0b' },
                      ].map((st, i) => (
                        <div key={i} className="stat-glass-card" style={{ '--accent': st.color, padding: 16 }}>
                          <div className="stat-card-glow" />
                          <div className="stat-icon-wrap" style={{ width: 40, height: 40 }}><span className="stat-icon" style={{ fontSize: '1.2rem' }}>{st.icon}</span></div>
                          <div className="stat-info">
                            <span className="stat-value" style={{ fontSize: '1.3rem' }}>{st.value}</span>
                            <span className="stat-label">{st.label}</span>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="dash-grid-2" style={{ marginBottom: 0 }}>
                      {/* General Info */}
                      <div className="glass-panel" style={{ padding: 20 }}>
                        <div className="panel-head" style={{ marginBottom: 16, paddingBottom: 12 }}><span className="panel-icon">🏢</span><h3>General</h3></div>
                        <div className="perf-metrics">
                          <div className="perf-row"><span className="perf-label">Client ID</span><span className="perf-value mono-text">{viewClient}</span></div>
                          <div className="perf-row"><span className="perf-label">Name</span><span className="perf-value">{t.name}</span></div>
                          <div className="perf-row"><span className="perf-label">API Key</span><span className="perf-value mono-text" style={{ fontSize: '.72rem' }}>{t.key}</span></div>
                          <div className="perf-row"><span className="perf-label">Status</span><span className={`status-dot ${t.active ? 'active' : 'inactive'}`}>{t.active ? 'Active' : 'Inactive'}</span></div>
                          <div className="perf-row"><span className="perf-label">API Integration</span><span className={`status-dot ${t.api_integration_enabled !== false ? 'active' : 'inactive'}`}>{t.api_integration_enabled !== false ? '✅ Enabled' : '❌ Disabled'}</span></div>
                          <div className="perf-row"><span className="perf-label">Features</span><div className="feature-tags">{(t.features || []).map((f, i) => <span key={i} className="feature-chip">{f}</span>)}</div></div>
                        </div>
                      </div>

                      {/* API Usage */}
                      <div className="glass-panel" style={{ padding: 20 }}>
                        <div className="panel-head" style={{ marginBottom: 16, paddingBottom: 12 }}><span className="panel-icon">📡</span><h3>API Usage</h3></div>
                        <div className="perf-metrics">
                          <div className="perf-row"><span className="perf-label">Plan</span><span className={`plan-badge ${t.plan || 'free'}`}>{pDef.label || (t.plan || 'free').toUpperCase()}</span></div>
                          <div className="perf-row"><span className="perf-label">API Limit</span><span className="perf-value">{t.api_limit === -1 ? 'Unlimited' : (t.api_limit || 0).toLocaleString()}</span></div>
                          <div className="perf-row"><span className="perf-label">API Used</span><span className="perf-value">{(t.api_used || 0).toLocaleString()}</span></div>
                          <div className="perf-row">
                            <span className="perf-label">Usage</span>
                            <div className="api-usage-cell" style={{ minWidth: 120 }}>
                              <div className="usage-bar-track"><div className="usage-bar-fill" style={{ width: `${t.api_limit === -1 ? 30 : usagePct}%`, background: usagePct > 90 ? 'linear-gradient(90deg,#ef4444,#f59e0b)' : undefined }} /></div>
                              <span className="usage-text">{t.api_limit === -1 ? '∞' : `${Math.round(usagePct)}%`}</span>
                            </div>
                          </div>
                        </div>
                        {s.doc_types && Object.keys(s.doc_types).length > 0 && (<>
                          <div className="panel-head" style={{ marginTop: 16, marginBottom: 12, paddingBottom: 10 }}><span className="panel-icon">📑</span><h3>Doc Types</h3></div>
                          <div className="perf-metrics">
                            {Object.entries(s.doc_types).map(([dt, cnt]) => (
                              <div key={dt} className="perf-row"><span className="perf-label">{dt}</span><span className="perf-value">{cnt}</span></div>
                            ))}
                          </div>
                        </>)}
                      </div>

                      {/* Contact */}
                      <div className="glass-panel" style={{ padding: 20 }}>
                        <div className="panel-head" style={{ marginBottom: 16, paddingBottom: 12 }}><span className="panel-icon">📞</span><h3>Contact</h3></div>
                        <div className="perf-metrics">
                          <div className="perf-row"><span className="perf-label">Email</span><span className="perf-value">{t.contact_email || '—'}</span></div>
                          <div className="perf-row"><span className="perf-label">Phone</span><span className="perf-value">{t.contact_phone || '—'}</span></div>
                          <div className="perf-row"><span className="perf-label">Address</span><span className="perf-value">{t.company_address || '—'}</span></div>
                          <div className="perf-row"><span className="perf-label">Notes</span><span className="perf-value" style={{ whiteSpace: 'pre-wrap', maxWidth: 300 }}>{t.notes || '—'}</span></div>
                        </div>
                      </div>

                      {/* Login */}
                      <div className="glass-panel" style={{ padding: 20 }}>
                        <div className="panel-head" style={{ marginBottom: 16, paddingBottom: 12 }}><span className="panel-icon">🔑</span><h3>Login Credentials</h3></div>
                        <div className="perf-metrics">
                          <div className="perf-row"><span className="perf-label">Username</span><span className="perf-value mono-text">{t.login_username || '—'}</span></div>
                          <div className="perf-row"><span className="perf-label">Password</span><span className="perf-value mono-text">{t.login_password || '—'}</span></div>
                        </div>
                      </div>
                    </div>
                  </>)}

                  {/* API Logs Tab */}
                  {clientDetailTab === 'api' && (<>
                    {!a ? <div className="dash-loader" style={{ minHeight: 200 }}><div className="loader-ring" /><p>Loading...</p></div> : (
                      <div className="table-wrap">
                        <table className="dash-table" style={{ minWidth: 700 }}>
                          <thead><tr><th>Time</th><th>Method</th><th>Endpoint</th><th>Status</th><th>Time (ms)</th><th>IP</th></tr></thead>
                          <tbody>
                            {(a.api_logs || []).map((l, i) => (
                              <tr key={i}>
                                <td><span className="text-muted">{l.created_at}</span></td>
                                <td><span className={`api-method method-${(l.method||'').toLowerCase()}`}>{l.method}</span></td>
                                <td><span className="mono-text">{l.endpoint}</span></td>
                                <td><span className="status-code-chip" style={{ '--sc-color': statusColor(l.status_code) }}>{l.status_code}</span></td>
                                <td>{l.response_time_ms || 0}</td>
                                <td><span className="text-muted">{l.ip_address}</span></td>
                              </tr>
                            ))}
                            {(a.api_logs || []).length === 0 && <tr><td colSpan={6} className="no-data">No API calls yet</td></tr>}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>)}

                  {/* Documents Tab */}
                  {clientDetailTab === 'docs' && (<>
                    {!a ? <div className="dash-loader" style={{ minHeight: 200 }}><div className="loader-ring" /><p>Loading...</p></div> : (
                      <div className="table-wrap">
                        <table className="dash-table" style={{ minWidth: 800 }}>
                          <thead><tr><th>Time</th><th>Type</th><th>Category</th><th>Filename</th><th>Engine</th><th>Time (ms)</th><th>Confidence</th><th>Status</th></tr></thead>
                          <tbody>
                            {(a.documents || []).map((d, i) => (
                              <tr key={i}>
                                <td><span className="text-muted">{d.created_at}</span></td>
                                <td><span className="feature-chip">{d.document_type}</span></td>
                                <td>{d.pdf_category || '—'}</td>
                                <td><span className="mono-text" style={{ fontSize: '.72rem' }}>{d.original_filename || '—'}</span></td>
                                <td>{d.engine_used || '—'}</td>
                                <td>{d.processing_time_ms || 0}</td>
                                <td>{d.confidence_score ? `${d.confidence_score}%` : '—'}</td>
                                <td><span className={`status-dot ${d.status === 'success' ? 'active' : 'inactive'}`}>{d.status}</span></td>
                              </tr>
                            ))}
                            {(a.documents || []).length === 0 && <tr><td colSpan={8} className="no-data">No documents yet</td></tr>}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>)}

                  {/* Notifications Tab */}
                  {clientDetailTab === 'notifications' && (<>
                    {!a ? <div className="dash-loader" style={{ minHeight: 200 }}><div className="loader-ring" /><p>Loading...</p></div> : (
                      <div className="table-wrap">
                        <table className="dash-table">
                          <thead><tr><th>Time</th><th>Type</th><th>Subject</th><th>Email</th><th>Via</th><th>Status</th></tr></thead>
                          <tbody>
                            {(a.notifications || []).map((n, i) => (
                              <tr key={i}>
                                <td><span className="text-muted">{n.created_at}</span></td>
                                <td><span className="feature-chip">{n.type}</span></td>
                                <td>{n.subject}</td>
                                <td><span className="text-muted">{n.recipient_email}</span></td>
                                <td>{n.sent_via}</td>
                                <td><span className={`status-dot ${n.status === 'sent' ? 'active' : 'inactive'}`}>{n.status}</span></td>
                              </tr>
                            ))}
                            {(a.notifications || []).length === 0 && <tr><td colSpan={6} className="no-data">No notifications yet</td></tr>}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>)}

                  {/* Billing Tab */}
                  {clientDetailTab === 'billing' && (<>
                    <div className="dash-grid-2" style={{ marginBottom: 20 }}>
                      <div className="glass-panel" style={{ padding: 20 }}>
                        <div className="panel-head" style={{ marginBottom: 16, paddingBottom: 12 }}><span className="panel-icon">💳</span><h3>Plan & Billing</h3></div>
                        <div className="perf-metrics">
                          <div className="perf-row"><span className="perf-label">Plan</span><span className={`plan-badge ${t.plan || 'free'}`}>{pDef.label || (t.plan || 'free').toUpperCase()}</span></div>
                          <div className="perf-row"><span className="perf-label">Price (Monthly)</span><span className="perf-value">{pDef.price_monthly != null ? formatRupee(pDef.price_monthly) : '—'}</span></div>
                          <div className="perf-row"><span className="perf-label">Price (Yearly)</span><span className="perf-value">{pDef.price_yearly != null ? formatRupee(pDef.price_yearly) : '—'}</span></div>
                          <div className="perf-row"><span className="perf-label">Billing Cycle</span><span className="perf-value">{t.billing_cycle === 'yearly' ? '📅 Yearly' : '📅 Monthly'}</span></div>
                          <div className="perf-row"><span className="perf-label">Plan Start</span><span className="perf-value">{t.plan_start || '—'}</span></div>
                          <div className="perf-row"><span className="perf-label">Plan End</span><span className="perf-value">{t.plan_end || '—'}</span></div>
                        </div>
                      </div>
                      <div className="glass-panel" style={{ padding: 20 }}>
                        <div className="panel-head" style={{ marginBottom: 16, paddingBottom: 12 }}><span className="panel-icon">💰</span><h3>Payment</h3></div>
                        <div className="perf-metrics">
                          <div className="perf-row"><span className="perf-label">Payment Status</span><span className={`pay-badge ${t.payment_status || 'trial'}`}>{(t.payment_status || 'trial').toUpperCase()}</span></div>
                          <div className="perf-row"><span className="perf-label">Last Payment</span><span className="perf-value">{t.last_payment_date || '—'}</span></div>
                          <div className="perf-row"><span className="perf-label">Total Revenue</span><span className="revenue-text" style={{ fontSize: '1.1rem' }}>{formatPrice(t.total_revenue || 0)}</span></div>
                          <div className="perf-row"><span className="perf-label">API Limit</span><span className="perf-value">{t.api_limit === -1 ? 'Unlimited' : (t.api_limit || 0).toLocaleString()}</span></div>
                          <div className="perf-row"><span className="perf-label">API Used</span><span className="perf-value">{(t.api_used || 0).toLocaleString()}</span></div>
                        </div>
                      </div>
                    </div>

                    {/* Transaction Summary */}
                    {clientTxns?.summary && (
                      <div className="stats-grid" style={{ marginBottom: 20 }}>
                        {[
                          { icon: '💰', label: 'Total Paid', value: formatPrice(clientTxns.summary.total_paid || 0), color: '#22c55e' },
                          { icon: '↩️', label: 'Refunds', value: formatPrice(clientTxns.summary.total_refund || 0), color: '#ef4444' },
                          { icon: '⏳', label: 'Pending', value: formatPrice(clientTxns.summary.total_pending || 0), color: '#f59e0b' },
                          { icon: '📊', label: 'Net Revenue', value: formatPrice(clientTxns.summary.net || 0), color: '#6366f1' },
                        ].map((st, i) => (
                          <div key={i} className="stat-glass-card" style={{ '--accent': st.color, padding: 16 }}>
                            <div className="stat-card-glow" />
                            <div className="stat-icon-wrap" style={{ width: 40, height: 40 }}><span className="stat-icon" style={{ fontSize: '1.2rem' }}>{st.icon}</span></div>
                            <div className="stat-info">
                              <span className="stat-value" style={{ fontSize: '1.2rem' }}>{st.value}</span>
                              <span className="stat-label">{st.label}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Transactions Table */}
                    <div className="glass-panel" style={{ padding: 20 }}>
                      <div className="panel-head" style={{ marginBottom: 16, paddingBottom: 12 }}>
                        <span className="panel-icon">🧾</span><h3>Transaction History ({clientTxns?.summary?.count || 0})</h3>
                        <button className="export-btn excel" style={{ marginLeft: 'auto', marginRight: 8 }} onClick={() => window.open(`${API_BASE_URL}/admin/tenants/${viewClient}/transactions/export`, '_blank')}>📥 Excel</button>
                        <button className="tenant-add-btn" onClick={() => setTxnModal({ type: 'payment', amount: 0, currency: 'INR', description: '', invoice_no: '', payment_method: '', reference_id: '', status: 'success', transaction_date: new Date().toISOString().slice(0,10) })}>+ Add Transaction</button>
                      </div>
                      <div className="table-wrap">
                        <table className="dash-table">
                          <thead><tr><th>Date</th><th>Type</th><th>Amount</th><th>Description</th><th>Invoice</th><th>Method</th><th>Reference</th><th>Status</th><th>Actions</th></tr></thead>
                          <tbody>
                            {(clientTxns?.transactions || []).map((tx) => (
                              <tr key={tx.id}>
                                <td><span className="text-muted">{tx.transaction_date || tx.created_at}</span></td>
                                <td><span className={`pay-badge ${tx.type === 'refund' ? 'overdue' : tx.type === 'payment' ? 'paid' : 'pending'}`}>{tx.type.toUpperCase()}</span></td>
                                <td><span className="revenue-text">{formatPrice(tx.amount || 0)}</span></td>
                                <td>{tx.description || '—'}</td>
                                <td><span className="mono-text">{tx.invoice_no || '—'}</span></td>
                                <td>{tx.payment_method || '—'}</td>
                                <td><span className="mono-text" style={{ fontSize: '.72rem' }}>{tx.reference_id || '—'}</span></td>
                                <td><span className={`status-dot ${tx.status === 'success' ? 'active' : tx.status === 'pending' ? '' : 'inactive'}`}>{tx.status}</span></td>
                                <td><button className="t-action-btn delete" onClick={() => deleteTransaction(tx.id)}>🗑️</button></td>
                              </tr>
                            ))}
                            {(clientTxns?.transactions || []).length === 0 && <tr><td colSpan={9} className="no-data">No transactions yet</td></tr>}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Add Transaction Modal */}
                    {txnModal && (
                      <div className="modal-overlay" onClick={() => setTxnModal(null)}>
                        <div className="modal-glass" onClick={e => e.stopPropagation()}>
                          <div className="modal-header"><h3>➕ Add Transaction</h3><button className="modal-close" onClick={() => setTxnModal(null)}>✕</button></div>
                          <div className="modal-body">
                            <div className="modal-row">
                              <div className="modal-field flex1">
                                <label>Type</label>
                                <select className="modal-select" value={txnModal.type} onChange={e => setTxnModal(p => ({...p, type: e.target.value}))}>
                                  {['payment','invoice','refund','credit','adjustment'].map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase()+t.slice(1)}</option>)}
                                </select>
                              </div>
                              <div className="modal-field flex1">
                                <label>Amount (paise)</label>
                                <input type="number" value={txnModal.amount} onChange={e => setTxnModal(p => ({...p, amount: parseInt(e.target.value)||0}))} />
                                <span className="field-hint">{formatPrice(txnModal.amount || 0)}</span>
                              </div>
                            </div>
                            <div className="modal-row">
                              <div className="modal-field flex1">
                                <label>Date</label>
                                <input type="date" value={txnModal.transaction_date} onChange={e => setTxnModal(p => ({...p, transaction_date: e.target.value}))} />
                              </div>
                              <div className="modal-field flex1">
                                <label>Status</label>
                                <select className="modal-select" value={txnModal.status} onChange={e => setTxnModal(p => ({...p, status: e.target.value}))}>
                                  {['success','pending','failed','cancelled'].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
                                </select>
                              </div>
                            </div>
                            <div className="modal-field"><label>Description</label><input type="text" value={txnModal.description} onChange={e => setTxnModal(p => ({...p, description: e.target.value}))} placeholder="Payment for Pro plan - Jan 2025" /></div>
                            <div className="modal-row">
                              <div className="modal-field flex1"><label>Invoice No</label><input type="text" value={txnModal.invoice_no} onChange={e => setTxnModal(p => ({...p, invoice_no: e.target.value}))} placeholder="INV-2025-001" /></div>
                              <div className="modal-field flex1"><label>Payment Method</label><input type="text" value={txnModal.payment_method} onChange={e => setTxnModal(p => ({...p, payment_method: e.target.value}))} placeholder="UPI / Card / Bank" /></div>
                            </div>
                            <div className="modal-field"><label>Reference ID</label><input type="text" value={txnModal.reference_id} onChange={e => setTxnModal(p => ({...p, reference_id: e.target.value}))} placeholder="TXN_123456" /></div>
                          </div>
                          <div className="modal-footer">
                            <button className="modal-btn cancel" onClick={() => setTxnModal(null)}>Cancel</button>
                            <button className="modal-btn save" onClick={saveTransaction}>Add Transaction</button>
                          </div>
                        </div>
                      </div>
                    )}
                  </>)}

                  {/* Config Tab */}
                  {clientDetailTab === 'config' && (
                    <div className="dash-grid-2" style={{ marginBottom: 0 }}>
                      <div className="glass-panel" style={{ padding: 20 }}>
                        <div className="panel-head" style={{ marginBottom: 16, paddingBottom: 12 }}><span className="panel-icon">🔗</span><h3>API Integration</h3></div>
                        <div className="perf-metrics">
                          <div className="perf-row"><span className="perf-label">Bank Statement</span><span className={`status-dot ${(t.features || []).includes('BANK_STATEMENT') ? 'active' : 'inactive'}`}>{(t.features || []).includes('BANK_STATEMENT') ? '✅ Enabled' : '❌ Disabled'}</span></div>
                          <div className="perf-row"><span className="perf-label">External API Access</span><span className={`status-dot ${t.api_integration_enabled !== false ? 'active' : 'inactive'}`}>{t.api_integration_enabled !== false ? '✅ Enabled' : '❌ Disabled'}</span></div>
                        </div>
                      </div>
                      <div className="glass-panel" style={{ padding: 20 }}>
                        <div className="panel-head" style={{ marginBottom: 16, paddingBottom: 12 }}><span className="panel-icon">⚙️</span><h3>Summary Config</h3></div>
                        <div className="perf-metrics">
                          <div className="perf-row"><span className="perf-label">Include Key Points</span><span className={`status-dot ${t.summary_config?.include_key_points ? 'active' : 'inactive'}`}>{t.summary_config?.include_key_points ? 'Yes' : 'No'}</span></div>
                          <div className="perf-row"><span className="perf-label">Include Full Text</span><span className={`status-dot ${t.summary_config?.include_full_text ? 'active' : 'inactive'}`}>{t.summary_config?.include_full_text ? 'Yes' : 'No'}</span></div>
                        </div>
                      </div>
                      <div className="glass-panel" style={{ padding: 20 }}>
                        <div className="panel-head" style={{ marginBottom: 16, paddingBottom: 12 }}><span className="panel-icon">🔐</span><h3>Features Access</h3></div>
                        <div className="feature-checkboxes">
                          {allFeatures.map(f => (
                            <span key={f} className={`feat-check ${(t.features || []).includes(f) ? 'checked' : ''}`} style={{ cursor: 'default' }}>
                              {(t.features || []).includes(f) ? '✅' : '❌'} {f}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
            </div>
          );
        })()}
      </div>
    );
  };

  const handleExcelDownload = () => {
    window.open(`${API_BASE_URL}/admin/tenants/export`, '_blank');
  };

  const handleCsvDownload = () => {
    const tList = Object.entries(tenants);
    const headers = ['Client ID','Name','Plan','Billing Cycle','Status','Plan Start','Plan End','API Limit','API Used','Payment Status','Last Payment','Total Revenue (₹)','Features','Email','Phone','Address','Notes'];
    const rows = tList.map(([id, t]) => [
      id, t.name, t.plan, t.billing_cycle, t.active ? 'Active' : 'Inactive',
      t.plan_start, t.plan_end,
      t.api_limit === -1 ? 'Unlimited' : t.api_limit, t.api_used,
      t.payment_status, t.last_payment_date,
      ((t.total_revenue || 0) / 100).toFixed(2),
      (t.features || []).join('; '), t.contact_email, t.contact_phone,
      t.company_address, t.notes
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v || '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `tenants_data_${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const sortData = (key) => {
    if (dataSortKey === key) setDataSortAsc(!dataSortAsc);
    else { setDataSortKey(key); setDataSortAsc(true); }
  };

  const getSortedFilteredTenants = () => {
    let list = Object.entries(tenants);
    if (dataSearch) {
      const q = dataSearch.toLowerCase();
      list = list.filter(([id, t]) =>
        id.toLowerCase().includes(q) || (t.name || '').toLowerCase().includes(q) ||
        (t.plan || '').toLowerCase().includes(q) || (t.contact_email || '').toLowerCase().includes(q) ||
        (t.payment_status || '').toLowerCase().includes(q)
      );
    }
    list.sort(([aId, a], [bId, b]) => {
      let va, vb;
      switch (dataSortKey) {
        case 'id': va = aId; vb = bId; break;
        case 'name': va = a.name || ''; vb = b.name || ''; break;
        case 'plan': va = a.plan || ''; vb = b.plan || ''; break;
        case 'revenue': va = a.total_revenue || 0; vb = b.total_revenue || 0; break;
        case 'api_used': va = a.api_used || 0; vb = b.api_used || 0; break;
        case 'payment': va = a.payment_status || ''; vb = b.payment_status || ''; break;
        case 'plan_start': va = a.plan_start || ''; vb = b.plan_start || ''; break;
        default: va = a.name || ''; vb = b.name || '';
      }
      if (typeof va === 'number') return dataSortAsc ? va - vb : vb - va;
      return dataSortAsc ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
    });
    return list;
  };

  const renderData = () => {
    const sorted = getSortedFilteredTenants();
    const totalRevenue = Object.values(tenants).reduce((s, t) => s + (t.total_revenue || 0), 0);
    const totalApiUsed = Object.values(tenants).reduce((s, t) => s + (t.api_used || 0), 0);
    const totalPages = Math.max(1, Math.ceil(sorted.length / dataPerPage));
    const safePage = Math.min(dataPage, totalPages);
    const paged = sorted.slice((safePage - 1) * dataPerPage, safePage * dataPerPage);

    const SortHeader = ({ label, sKey }) => (
      <th className="sortable-th" onClick={() => sortData(sKey)}>
        {label} {dataSortKey === sKey ? (dataSortAsc ? '▲' : '▼') : ''}
      </th>
    );

    return (
      <div className="dash-section">
        <div className="stats-grid">
          {[
            { icon: '🗂️', label: 'Total Records', value: Object.keys(tenants).length, color: '#6366f1' },
            { icon: '💰', label: 'Total Revenue', value: formatPrice(totalRevenue), color: '#22c55e' },
            { icon: '📡', label: 'Total API Calls', value: totalApiUsed.toLocaleString(), color: '#f59e0b' },
            { icon: '📊', label: 'Filtered', value: sorted.length, color: '#8b5cf6' },
          ].map((s, i) => (
            <div key={i} className="stat-glass-card" style={{ '--accent': s.color }}>
              <div className="stat-card-glow" />
              <div className="stat-icon-wrap"><span className="stat-icon">{s.icon}</span></div>
              <div className="stat-info">
                <span className="stat-value">{s.value}</span>
                <span className="stat-label">{s.label}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="glass-panel">
          <div className="data-panel-head">
            <div className="data-panel-top">
              <span className="panel-icon">🗂️</span>
              <h3>Data Table</h3>
            </div>
            <div className="data-toolbar">
              <div className="data-search-wrap">
                <span>🔍</span>
                <input type="text" className="data-search" placeholder="Search by name, ID, plan, email, payment..." value={dataSearch} onChange={e => { setDataSearch(e.target.value); setDataPage(1); }} />
              </div>
              <select className="per-page-select" value={dataPerPage} onChange={e => { setDataPerPage(Number(e.target.value)); setDataPage(1); }}>
                {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n} / page</option>)}
              </select>
              <button className="export-btn excel" onClick={handleExcelDownload}>📥 Excel</button>
              <button className="export-btn csv" onClick={handleCsvDownload}>📥 CSV</button>
            </div>
          </div>
          <div className="data-table-wrap">
            <table className="dash-table data-table">
              <thead>
                <tr>
                  <SortHeader label="Client ID" sKey="id" />
                  <SortHeader label="Name" sKey="name" />
                  <SortHeader label="Plan" sKey="plan" />
                  <th>Billing</th>
                  <SortHeader label="Plan Start" sKey="plan_start" />
                  <th>Plan End</th>
                  <th>API Limit</th>
                  <SortHeader label="API Used" sKey="api_used" />
                  <SortHeader label="Payment" sKey="payment" />
                  <th>Last Payment</th>
                  <SortHeader label="Revenue" sKey="revenue" />
                  <th>Features</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Address</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {paged.map(([id, t]) => (
                  <tr key={id}>
                    <td><span className="mono-text">{id}</span></td>
                    <td><span className="tenant-name">{t.name}</span></td>
                    <td><span className={`plan-badge ${t.plan || 'free'}`}>{(t.plan || 'free').toUpperCase()}</span></td>
                    <td>{t.billing_cycle === 'yearly' ? 'Yearly' : 'Monthly'}</td>
                    <td>{t.plan_start || '—'}</td>
                    <td>{t.plan_end || '—'}</td>
                    <td>{t.api_limit === -1 ? '∞' : (t.api_limit || 0).toLocaleString()}</td>
                    <td>{(t.api_used || 0).toLocaleString()}</td>
                    <td><span className={`pay-badge ${t.payment_status || 'trial'}`}>{(t.payment_status || 'trial').toUpperCase()}</span></td>
                    <td>{t.last_payment_date || '—'}</td>
                    <td><span className="revenue-text">{formatPrice(t.total_revenue || 0)}</span></td>
                    <td><div className="feature-tags">{(t.features || []).map((f, i) => <span key={i} className="feature-chip">{f}</span>)}</div></td>
                    <td><span className="text-muted">{t.contact_email || '—'}</span></td>
                    <td><span className="text-muted">{t.contact_phone || '—'}</span></td>
                    <td><span className="text-muted">{t.company_address || '—'}</span></td>
                    <td><span className={`status-dot ${t.active ? 'active' : 'inactive'}`}>{t.active ? 'Active' : 'Inactive'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {sorted.length === 0 && <div className="no-data">No tenants match your search.</div>}

          {/* Pagination */}
          {sorted.length > 0 && (
            <div className="pagination">
              <span className="page-info">Showing {(safePage - 1) * dataPerPage + 1}–{Math.min(safePage * dataPerPage, sorted.length)} of {sorted.length}</span>
              <div className="page-btns">
                <button className="page-btn" disabled={safePage <= 1} onClick={() => setDataPage(1)}>«</button>
                <button className="page-btn" disabled={safePage <= 1} onClick={() => setDataPage(safePage - 1)}>‹</button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let p;
                  if (totalPages <= 5) p = i + 1;
                  else if (safePage <= 3) p = i + 1;
                  else if (safePage >= totalPages - 2) p = totalPages - 4 + i;
                  else p = safePage - 2 + i;
                  return (
                    <button key={p} className={`page-btn ${safePage === p ? 'active' : ''}`} onClick={() => setDataPage(p)}>{p}</button>
                  );
                })}
                <button className="page-btn" disabled={safePage >= totalPages} onClick={() => setDataPage(safePage + 1)}>›</button>
                <button className="page-btn" disabled={safePage >= totalPages} onClick={() => setDataPage(totalPages)}>»</button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderAPI = () => {
    const eps = apiStats?.endpoints || [];
    const totalCalls = apiStats?.total_calls || 0;
    const totalSuccess = eps.reduce((s, e) => s + e.success, 0);
    const totalFailed = eps.reduce((s, e) => s + e.failed, 0);
    const avgMs = eps.length ? Math.round(eps.reduce((s, e) => s + e.avg_ms, 0) / eps.length) : 0;

    const statusColor = (s) => {
      if (s === 0) return '#ef4444';
      if (s < 300) return '#22c55e';
      if (s < 400) return '#f59e0b';
      return '#ef4444';
    };

    return (
      <div className="dash-section">
        {/* Stats */}
        <div className="stats-grid">
          {[
            { icon: '📡', label: 'Total API Calls', value: totalCalls.toLocaleString(), color: '#6366f1' },
            { icon: '✅', label: 'Successful', value: totalSuccess.toLocaleString(), color: '#22c55e' },
            { icon: '❌', label: 'Failed', value: totalFailed.toLocaleString(), color: '#ef4444' },
            { icon: '⚡', label: 'Avg Response', value: `${avgMs}ms`, color: '#f59e0b' },
          ].map((s, i) => (
            <div key={i} className="stat-glass-card" style={{ '--accent': s.color }}>
              <div className="stat-card-glow" />
              <div className="stat-icon-wrap"><span className="stat-icon">{s.icon}</span></div>
              <div className="stat-info">
                <span className="stat-value">{s.value}</span>
                <span className="stat-label">{s.label}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Per-endpoint stats table */}
        <div className="glass-panel">
          <div className="panel-head">
            <span className="panel-icon">📊</span>
            <h3>Endpoint Statistics</h3>
            <button className="tenant-add-btn" style={{ fontSize: '.78rem', padding: '6px 14px' }} onClick={fetchApiStats}>🔄 Refresh</button>
          </div>
          <div className="table-wrap">
            <table className="dash-table">
              <thead>
                <tr>
                  <th>Method</th>
                  <th>Endpoint</th>
                  <th>Total Calls</th>
                  <th>Success</th>
                  <th>Failed</th>
                  <th>Success Rate</th>
                  <th>Avg Time</th>
                  <th>Status Codes</th>
                </tr>
              </thead>
              <tbody>
                {eps.map((e, i) => (
                  <tr key={i}>
                    <td><span className={`api-method method-${e.method.toLowerCase()}`}>{e.method}</span></td>
                    <td><span className="mono-text">{e.endpoint}</span></td>
                    <td><span style={{ fontWeight: 700 }}>{e.total.toLocaleString()}</span></td>
                    <td><span style={{ color: '#86efac', fontWeight: 600 }}>{e.success}</span></td>
                    <td><span style={{ color: e.failed > 0 ? '#fca5a5' : 'rgba(255,255,255,.3)', fontWeight: 600 }}>{e.failed}</span></td>
                    <td>
                      <div className="api-usage-cell">
                        <div className="usage-bar-track" style={{ width: 80 }}>
                          <div className="usage-bar-fill" style={{ width: `${e.total ? (e.success / e.total * 100) : 0}%`,
                            background: e.total && e.success / e.total < 0.8 ? 'linear-gradient(90deg,#ef4444,#f59e0b)' : undefined }} />
                        </div>
                        <span className="usage-text">{e.total ? Math.round(e.success / e.total * 100) : 0}%</span>
                      </div>
                    </td>
                    <td><span className="perf-value" style={{ fontSize: '.78rem' }}>{e.avg_ms}ms</span></td>
                    <td>
                      <div className="status-code-chips">
                        {Object.entries(e.by_status).map(([code, cnt]) => (
                          <span key={code} className="status-code-chip" style={{ '--sc-color': statusColor(Number(code)) }}>
                            {code}: {cnt}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
                {eps.length === 0 && <tr><td colSpan={8} className="no-data">No API calls recorded yet</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent API Calls */}
        {apiStats?.recent_calls?.length > 0 && (
          <div className="glass-panel" style={{ marginTop: 20 }}>
            <div className="panel-head">
              <span className="panel-icon">🕐</span>
              <h3>Recent API Calls</h3>
              <span className="text-muted" style={{ fontSize: '.75rem', marginLeft: 8 }}>{apiStats.recent_calls.length} calls</span>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <div style={{ maxHeight: 420, overflowY: 'auto' }}>
                <table className="dash-table" style={{ minWidth: 800 }}>
                  <thead style={{ position: 'sticky', top: 0, zIndex: 2, background: 'rgba(15,15,30,.95)', backdropFilter: 'blur(8px)' }}>
                    <tr>
                      <th>Time</th>
                      <th>Method</th>
                      <th>Endpoint</th>
                      <th>Status</th>
                      <th>Response Time</th>
                      <th>Client</th>
                      <th>IP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {apiStats.recent_calls.map((c, i) => (
                      <tr key={i}>
                        <td><span className="text-muted">{c.timestamp}</span></td>
                        <td><span className={`api-method method-${c.method.toLowerCase()}`}>{c.method}</span></td>
                        <td><span className="mono-text">{c.endpoint}</span></td>
                        <td><span className="status-code-chip" style={{ '--sc-color': statusColor(c.status) }}>{c.status}</span></td>
                        <td>{c.time_ms}ms</td>
                        <td>{c.client}</td>
                        <td><span className="text-muted">{c.ip}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Existing endpoint docs */}
        <div className="glass-panel" style={{ marginTop: 20 }}>
          <div className="panel-head">
            <span className="panel-icon">📖</span>
            <h3>API Documentation</h3>
          </div>
          <div className="api-endpoint-list">
            {healthData?.api_intelligence?.endpoints && Object.entries(healthData.api_intelligence.endpoints).map(([section, endpoints]) => (
              <div key={section} className="api-section-block">
                <h4 className="api-section-title">{section.replace(/_/g, ' ')}</h4>
                {Object.entries(endpoints).map(([endpoint, info]) => (
                  <div key={endpoint} className="api-endpoint-row">
                    <span className={`api-method method-${endpoint.split(' ')[0].toLowerCase()}`}>{endpoint.split(' ')[0]}</span>
                    <span className="api-path">{endpoint.split(' ')[1]}</span>
                    <span className="api-desc">{info.description}</span>
                    <span className={`api-auth-badge ${info.authentication === 'Not required' ? 'public' : 'private'}`}>
                      {info.authentication === 'Not required' ? '🔓 Public' : '🔒 Auth'}
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* ═══ POSTMAN-LIKE API TESTER ═══ */}
        <div className="glass-panel api-tester-panel" style={{ marginTop: 20 }}>
          <div className="panel-head">
            <span className="panel-icon">🧪</span>
            <h3>API Tester</h3>
          </div>

          {/* Client Selector */}
          <div className="tester-client-bar">
            <span className="tester-client-label">🔑 Client:</span>
            <select className="tester-client-select" value="" onChange={e => {
              const cid = e.target.value;
              if (!cid) return;
              const t = tenants[cid];
              if (!t) return;
              setTester(p => ({
                ...p,
                body: JSON.stringify({ Authorization: t.key, client_name: cid }, null, 2),
                headers: p.headers.map(h => h.key === 'Authorization'
                  ? { ...h, value: `Bearer ${p._lastToken || '<get_token_from_/auth>'}` } : h),
                _selectedClient: cid,
              }));
            }}>
              <option value="">Select a client to auto-fill credentials...</option>
              {Object.entries(tenants).map(([cid, t]) => (
                <option key={cid} value={cid}>{t.name} ({cid}) — {t.active ? '✅' : '❌'} {(t.plan || 'free').toUpperCase()}</option>
              ))}
            </select>
            {tester._selectedClient && tenants[tester._selectedClient] && (
              <div className="tester-client-info">
                <span className="tester-cred-chip">📋 {tester._selectedClient}</span>
                <span className="tester-cred-chip">🔐 {tenants[tester._selectedClient].key}</span>
                <span className={`plan-badge ${tenants[tester._selectedClient].plan || 'free'}`}>
                  {(tenants[tester._selectedClient].plan || 'free').toUpperCase()}
                </span>
                <span className="tester-cred-chip">Features: {(tenants[tester._selectedClient].features || []).join(', ')}</span>
              </div>
            )}
          </div>

          {/* Presets */}
          <div className="tester-presets">
            {testerPresets.map((p, i) => (
              <button key={i} className="tester-preset-btn" onClick={() => {
                const cid = tester._selectedClient || '';
                const t = cid ? tenants[cid] : null;
                let body = p.body || '';
                let hdrs = p.headers.length ? [...p.headers.map(h => ({...h}))] : [{ key: '', value: '', enabled: true }];
                if (p.label === 'Auth' && t) {
                  body = JSON.stringify({ Authorization: t.key, client_name: cid }, null, 2);
                }
                if (t && hdrs.some(h => h.key === 'Authorization')) {
                  hdrs = hdrs.map(h => h.key === 'Authorization'
                    ? { ...h, value: `Bearer ${tester._lastToken || '<get_token_from_/auth>'}` } : h);
                }
                setTester(prev => ({
                  ...prev, method: p.method, url: p.url, headers: hdrs,
                  body, bodyType: p.bodyType || 'json',
                  formFields: p.formFields ? p.formFields.map(f => ({...f})) : [{ key: '', value: '', type: 'text', file: null }],
                  response: null,
                }));
              }}>
                <span className={`api-method method-${p.method.toLowerCase()}`}>{p.method}</span> {p.label}
              </button>
            ))}
          </div>

          {/* URL Bar */}
          <div className="tester-url-bar">
            <select className="tester-method-select" value={tester.method}
              onChange={e => setTester(p => ({ ...p, method: e.target.value }))}>
              {['GET','POST','PUT','PATCH','DELETE'].map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            <input className="tester-url-input" type="text" value={tester.url}
              onChange={e => setTester(p => ({ ...p, url: e.target.value }))}
              placeholder="Enter request URL" />
            <button className="tester-send-btn" onClick={sendTestRequest} disabled={tester.sending}>
              {tester.sending ? '⏳ Sending...' : '🚀 Send'}
            </button>
          </div>

          {/* Request Tabs */}
          <div className="tester-tabs">
            {[['headers', '📋 Headers'], ['body', '📝 Body']].map(([k, l]) => (
              <button key={k} className={`tester-tab ${testerTab === k ? 'active' : ''}`}
                onClick={() => setTesterTab(k)}>{l}</button>
            ))}
          </div>

          {/* Headers */}
          {testerTab === 'headers' && (
            <div className="tester-headers">
              {tester.headers.map((h, i) => (
                <div key={i} className="tester-header-row">
                  <input type="checkbox" checked={h.enabled}
                    onChange={e => updateTesterHeader(i, 'enabled', e.target.checked)} />
                  <input type="text" className="tester-hdr-key" placeholder="Key" value={h.key}
                    onChange={e => updateTesterHeader(i, 'key', e.target.value)} />
                  <input type="text" className="tester-hdr-val" placeholder="Value" value={h.value}
                    onChange={e => updateTesterHeader(i, 'value', e.target.value)} />
                  <button className="tester-hdr-del" onClick={() => removeTesterHeader(i)}>✕</button>
                </div>
              ))}
              <button className="tester-add-hdr" onClick={addTesterHeader}>+ Add Header</button>
            </div>
          )}

          {/* Body */}
          {testerTab === 'body' && (<>
            {/* Body type toggle */}
            <div className="tester-body-type-bar">
              {[['json', 'JSON'], ['form-data', 'Form Data']].map(([k, l]) => (
                <button key={k} className={`tester-body-type-btn ${tester.bodyType === k ? 'active' : ''}`}
                  onClick={() => setTester(p => ({ ...p, bodyType: k }))}>{l}</button>
              ))}
              {tester.bodyType === 'form-data' && (
                <span className="tester-body-hint">💡 Use type "file" to attach files (images, PDFs)</span>
              )}
            </div>

            {tester.bodyType === 'json' ? (
              <textarea className="tester-body-editor" rows={6} value={tester.body}
                onChange={e => setTester(p => ({ ...p, body: e.target.value }))}
                placeholder='{ "key": "value" }' spellCheck={false} />
            ) : (
              <div className="tester-form-fields">
                {tester.formFields.map((f, i) => (
                  <div key={i} className="tester-form-row">
                    <select className="tester-form-type" value={f.type}
                      onChange={e => updateFormField(i, 'type', e.target.value)}>
                      <option value="text">Text</option>
                      <option value="file">File</option>
                    </select>
                    <input type="text" className="tester-hdr-key" placeholder="Key" value={f.key}
                      onChange={e => updateFormField(i, 'key', e.target.value)} />
                    {f.type === 'text' ? (
                      <input type="text" className="tester-hdr-val" placeholder="Value" value={f.value}
                        onChange={e => updateFormField(i, 'value', e.target.value)} />
                    ) : (
                      <label className="tester-file-input">
                        <input type="file" onChange={e => updateFormField(i, 'file', e.target.files[0] || null)} />
                        <span className="tester-file-label">
                          {f.file ? `📎 ${f.file.name} (${(f.file.size / 1024).toFixed(1)} KB)` : '📁 Choose file...'}
                        </span>
                      </label>
                    )}
                    <button className="tester-hdr-del" onClick={() => removeFormField(i)}>✕</button>
                  </div>
                ))}
                <button className="tester-add-hdr" onClick={addFormField}>+ Add Field</button>
              </div>
            )}
          </>)}

          {/* Response */}
          {tester.response && (
            <div className="tester-response">
              <div className="tester-resp-bar">
                <span className="tester-resp-status" style={{ color: statusColor(tester.response.status) }}>
                  {tester.response.status} {tester.response.statusText}
                </span>
                <span className="tester-resp-meta">⏱ {tester.response.time}ms</span>
                <span className="tester-resp-meta">📦 {tester.response.size > 1024 ? `${(tester.response.size / 1024).toFixed(1)} KB` : `${tester.response.size} B`}</span>
                <div style={{ flex: 1 }} />
                <div className="tester-tabs" style={{ marginBottom: 0 }}>
                  {[['body', 'Body'], ['headers', 'Headers']].map(([k, l]) => (
                    <button key={k} className={`tester-tab ${respTab === k ? 'active' : ''}`}
                      onClick={() => setRespTab(k)}>{l}</button>
                  ))}
                </div>
              </div>
              {respTab === 'body' ? (
                <pre className="tester-resp-body">{tester.response.body}</pre>
              ) : (
                <div className="tester-resp-headers">
                  {Object.entries(tester.response.headers).map(([k, v]) => (
                    <div key={k} className="perf-row">
                      <span className="perf-label">{k}</span>
                      <span className="perf-value">{v}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderDocuments = () => (
    <div className="dash-section">
      <div className="dash-grid-2">
        {healthData?.document_capabilities?.supported_documents?.government_ids && Object.entries(healthData.document_capabilities.supported_documents.government_ids).map(([type, info]) => (
          <div key={type} className="glass-panel doc-card">
            <div className="panel-head">
              <span className="panel-icon">🆔</span>
              <h3>{type}</h3>
            </div>
            <div className="doc-details">
              <div className="doc-row"><span>Full Name</span><span>{info.full_name}</span></div>
              <div className="doc-row"><span>Accuracy</span><span className="accent-text">{info.accuracy}</span></div>
              <div className="doc-row"><span>Processing</span><span>{info.processing_time}</span></div>
            </div>
          </div>
        ))}
        {healthData?.document_capabilities?.supported_documents?.pdf_intelligence && Object.entries(healthData.document_capabilities.supported_documents.pdf_intelligence).map(([type, info]) => (
          <div key={type} className="glass-panel doc-card">
            <div className="panel-head">
              <span className="panel-icon">📄</span>
              <h3>{type}</h3>
            </div>
            <div className="doc-details">
              <div className="doc-row"><span>Accuracy</span><span className="accent-text">{info.accuracy}</span></div>
              <div className="doc-row"><span>Processing</span><span>{info.processing_time || 'Variable'}</span></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderSecurity = () => (
    <div className="dash-section">
      <div className="dash-grid-2">
        <div className="glass-panel">
          <div className="panel-head">
            <span className="panel-icon">🔐</span>
            <h3>Authentication</h3>
          </div>
          <div className="perf-metrics">
            {healthData?.security_intelligence?.authentication_system && Object.entries(healthData.security_intelligence.authentication_system).map(([k, v]) => (
              <div key={k} className="perf-row">
                <span className="perf-label">{k.replace(/_/g, ' ')}</span>
                <span className="perf-value">{v}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="glass-panel">
          <div className="panel-head">
            <span className="panel-icon">🛡️</span>
            <h3>Security Features</h3>
          </div>
          <div className="security-list">
            {healthData?.security_intelligence?.security_features?.map((f, i) => (
              <div key={i} className="security-item">
                <span className="check-icon">✅</span>
                <span>{f}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderSystem = () => (
    <div className="dash-section">
      <div className="dash-grid-2">
        <div className="glass-panel">
          <div className="panel-head">
            <span className="panel-icon">💻</span>
            <h3>Server Info</h3>
          </div>
          <div className="perf-metrics">
            {healthData?.system_intelligence?.server_info && Object.entries(healthData.system_intelligence.server_info).map(([k, v]) => (
              <div key={k} className="perf-row">
                <span className="perf-label">{k.replace(/_/g, ' ')}</span>
                <span className="perf-value">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="glass-panel">
          <div className="panel-head">
            <span className="panel-icon">🛠️</span>
            <h3>Technology Stack</h3>
          </div>
          <div className="tech-stack-list">
            <div className="perf-row">
              <span className="perf-label">Backend</span>
              <span className="perf-value">{healthData?.technology_stack?.backend_framework}</span>
            </div>
            <div className="tech-libs">
              {healthData?.technology_stack?.ai_libraries?.map((lib, i) => (
                <span key={i} className="feature-chip">{lib}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAnalytics = () => {
    const a = analyticsData;
    if (!a) return <div className="dash-loader"><div className="loader-ring" /><p>Loading analytics...</p></div>;

    const api = a.api_calls || {};
    const docs = a.documents || {};
    const perf = a.performance || {};
    const stor = a.storage || {};

    const statCards = [
      { icon: '📡', label: 'Total API Calls', value: api.total || 0, color: '#6366f1' },
      { icon: '✅', label: 'Auth Success', value: api.auth_success || 0, color: '#22c55e' },
      { icon: '❌', label: 'Auth Failed', value: api.auth_failed || 0, color: '#ef4444' },
      { icon: '📄', label: 'Docs Processed', value: docs.total_processed || 0, color: '#f59e0b' },
      { icon: '🎯', label: 'Success Rate', value: `${api.success_rate || 0}%`, color: '#8b5cf6' },
      { icon: '💾', label: 'Storage', value: `${stor.total_storage_mb || 0} MB`, color: '#ec4899' },
    ];

    return (
      <div className="dash-section">
        <div className="stats-grid">
          {statCards.map((s, i) => (
            <div key={i} className="stat-glass-card" style={{ '--accent': s.color }}>
              <div className="stat-card-glow" />
              <div className="stat-icon-wrap"><span className="stat-icon">{s.icon}</span></div>
              <div className="stat-info">
                <span className="stat-value">{s.value}</span>
                <span className="stat-label">{s.label}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="dash-grid-2">
          {/* Docs by Type */}
          <div className="glass-panel">
            <div className="panel-head"><span className="panel-icon">🆔</span><h3>Documents by Type</h3></div>
            <div className="perf-metrics">
              {docs.by_type && Object.entries(docs.by_type).map(([k, v]) => (
                <div key={k} className="perf-row">
                  <span className="perf-label">{k}</span>
                  <span className="perf-value">{v}</span>
                </div>
              ))}
              {(!docs.by_type || Object.keys(docs.by_type).length === 0) && <div className="perf-row"><span className="perf-label text-muted">No documents yet</span></div>}
            </div>
          </div>

          {/* PDF Categories */}
          <div className="glass-panel">
            <div className="panel-head"><span className="panel-icon">📑</span><h3>PDF Categories</h3></div>
            <div className="perf-metrics">
              {docs.by_pdf_category && Object.entries(docs.by_pdf_category).map(([k, v]) => (
                <div key={k} className="perf-row">
                  <span className="perf-label">{k}</span>
                  <span className="perf-value">{v}</span>
                </div>
              ))}
              {(!docs.by_pdf_category || Object.keys(docs.by_pdf_category).length === 0) && <div className="perf-row"><span className="perf-label text-muted">No PDFs yet</span></div>}
            </div>
          </div>

          {/* Performance */}
          <div className="glass-panel">
            <div className="panel-head"><span className="panel-icon">⏱️</span><h3>Performance</h3></div>
            <div className="perf-metrics">
              {Object.entries(perf).map(([k, v]) => (
                <div key={k} className="perf-row">
                  <span className="perf-label">{k.replace(/_/g, ' ')}</span>
                  <span className="perf-value">{v}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Engine Usage */}
          <div className="glass-panel">
            <div className="panel-head"><span className="panel-icon">🧠</span><h3>Engine Usage</h3></div>
            <div className="engine-list">
              {a.engine_usage && Object.entries(a.engine_usage).map(([eng, count]) => (
                <div key={eng} className="engine-row">
                  <span className="engine-name">{eng}</span>
                  <span className="engine-badge">{count} calls</span>
                </div>
              ))}
              {(!a.engine_usage || Object.keys(a.engine_usage).length === 0) && <div className="perf-row"><span className="perf-label text-muted">No engine data yet</span></div>}
            </div>
          </div>

          {/* Client Usage */}
          <div className="glass-panel">
            <div className="panel-head"><span className="panel-icon">🏢</span><h3>Client Usage</h3></div>
            <div className="engine-list">
              {a.client_usage && Object.entries(a.client_usage).map(([client, info]) => (
                <div key={client} className="engine-row">
                  <div className="engine-info">
                    <span className="engine-name">{client}</span>
                    <span className="engine-type">Auth: {info.auth} | Process: {info.process} | Docs: {info.docs_processed}</span>
                  </div>
                  <span className="engine-badge">{info.auth + info.process} total</span>
                </div>
              ))}
              {(!a.client_usage || Object.keys(a.client_usage).length === 0) && <div className="perf-row"><span className="perf-label text-muted">No client data yet</span></div>}
            </div>
          </div>

          {/* Hourly Activity */}
          <div className="glass-panel">
            <div className="panel-head"><span className="panel-icon">🕐</span><h3>Hourly Activity</h3></div>
            <div className="perf-metrics">
              {a.hourly_activity && Object.entries(a.hourly_activity).sort().map(([h, c]) => (
                <div key={h} className="perf-row">
                  <span className="perf-label">{h}:00</span>
                  <span className="perf-value">{c} calls</span>
                </div>
              ))}
              {(!a.hourly_activity || Object.keys(a.hourly_activity).length === 0) && <div className="perf-row"><span className="perf-label text-muted">No hourly data yet</span></div>}
            </div>
          </div>
        </div>

        {/* Daily Activity */}
        {a.daily_activity && Object.keys(a.daily_activity).length > 0 && (
          <div className="glass-panel">
            <div className="panel-head"><span className="panel-icon">📅</span><h3>Daily Activity</h3></div>
            <div className="daily-activity-grid">
              {Object.entries(a.daily_activity).sort().reverse().slice(0, 14).map(([date, info]) => {
                const maxVal = Math.max(...Object.values(a.daily_activity).map(d => d.api_calls || d));
                const val = typeof info === 'object' ? info.api_calls : info;
                const docVal = typeof info === 'object' ? info.docs_processed : 0;
                return (
                  <div key={date} className="daily-bar-item">
                    <div className="daily-bar-track">
                      <div className="daily-bar-fill" style={{ height: `${Math.min(100, (val / Math.max(maxVal, 1)) * 100)}%` }} />
                    </div>
                    <span className="daily-bar-label">{date.slice(5)}</span>
                    <span className="daily-bar-count">{val}{docVal ? ` / ${docVal}` : ''}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Recent Activity */}
        {a.recent_activity && a.recent_activity.length > 0 && (
          <div className="glass-panel" style={{ marginTop: 20 }}>
            <div className="panel-head"><span className="panel-icon">🕐</span><h3>Recent Activity</h3></div>
            <div className="log-lines-wrap">
              {a.recent_activity.slice(0, 20).map((item, i) => (
                <div key={i} className="log-line">
                  <span className="text-muted">{item.timestamp}</span> — {item.event}: {item.detail}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderLogs = () => {
    if (!logsData) return <div className="dash-loader"><div className="loader-ring" /><p>Loading logs...</p></div>;

    const allFiles = logsData.log_files || [];
    const infoCount = allFiles.filter(f => f.type === 'info').length;
    const errorCount = allFiles.filter(f => f.type === 'error').length;

    return (
      <div className="dash-section">
        <div className="stats-grid">
          <div className="stat-glass-card" style={{ '--accent': '#6366f1' }}>
            <div className="stat-card-glow" />
            <div className="stat-icon-wrap"><span className="stat-icon">📁</span></div>
            <div className="stat-info">
              <span className="stat-value">{infoCount}</span>
              <span className="stat-label">Info Log Files</span>
            </div>
          </div>
          <div className="stat-glass-card" style={{ '--accent': '#ef4444' }}>
            <div className="stat-card-glow" />
            <div className="stat-icon-wrap"><span className="stat-icon">⚠️</span></div>
            <div className="stat-info">
              <span className="stat-value">{errorCount}</span>
              <span className="stat-label">Error Log Files</span>
            </div>
          </div>
        </div>

        <div className="glass-panel">
          <div className="panel-head"><span className="panel-icon">📋</span><h3>Log Files</h3></div>
          <div className="table-wrap">
            <table className="dash-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Filename</th>
                  <th>Size</th>
                  <th>Modified</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {allFiles.map((f, i) => (
                  <tr key={i} className={activeLog?.filename === f.filename ? 'active-log-row' : ''}>
                    <td><span className={`log-type-badge ${f.type}`}>{f.type.toUpperCase()}</span></td>
                    <td><span className="mono-text">{f.filename}</span></td>
                    <td>{f.size_kb} KB</td>
                    <td>{f.modified}</td>
                    <td>
                      <div className="log-actions">
                        <button className="log-action-btn view" onClick={() => fetchLogContent(f.type, f.filename)}>👁️ View</button>
                        <button className="log-action-btn download" onClick={() => handleLogDownload(f.type, f.filename)}>⬇️ Download</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {logContent && (
          <div className="glass-panel" style={{ marginTop: 20 }}>
            <div className="panel-head">
              <span className="panel-icon">📄</span>
              <h3>{activeLog?.type}/{activeLog?.filename}</h3>
              <button className="log-close-btn" onClick={() => { setLogContent(null); setActiveLog(null); }}>✕</button>
            </div>
            <div className="log-viewer">
              <pre className="log-content">{logContent}</pre>
            </div>
          </div>
        )}
      </div>
    );
  };

  const sendNotification = async (clientId, type, customMsg) => {
    const key = `${clientId}_${type}`;
    setNotifyStatus(prev => ({ ...prev, [key]: 'sending' }));
    try {
      const res = await fetch(`${API_BASE_URL}/admin/notify`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_id: clientId, type, custom_message: customMsg || '' }),
      });
      const data = await res.json();
      setNotifyStatus(prev => ({ ...prev, [key]: data.success ? 'sent' : 'failed' }));
      setTimeout(() => setNotifyStatus(prev => ({ ...prev, [key]: null })), 3000);
    } catch {
      setNotifyStatus(prev => ({ ...prev, [key]: 'failed' }));
      setTimeout(() => setNotifyStatus(prev => ({ ...prev, [key]: null })), 3000);
    }
  };

  const renderPayments = () => {
    const tList = Object.entries(tenants);
    const totalRevenue = tList.reduce((s, [, t]) => s + (t.total_revenue || 0), 0);
    const paidCount = tList.filter(([, t]) => t.payment_status === 'paid').length;
    const pendingCount = tList.filter(([, t]) => t.payment_status === 'pending').length;
    const overdueCount = tList.filter(([, t]) => t.payment_status === 'overdue').length;
    const trialCount = tList.filter(([, t]) => t.payment_status === 'trial').length;
    const monthlyRev = tList.filter(([, t]) => t.billing_cycle === 'monthly').reduce((s, [, t]) => s + (t.total_revenue || 0), 0);
    const yearlyRev = tList.filter(([, t]) => t.billing_cycle === 'yearly').reduce((s, [, t]) => s + (t.total_revenue || 0), 0);

    const today = new Date().toISOString().slice(0, 10);
    const soon = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
    const expiringSoon = tList.filter(([, t]) => t.plan_end && t.plan_end >= today && t.plan_end <= soon);
    const expired = tList.filter(([, t]) => t.plan_end && t.plan_end < today);

    const NotifyBtn = ({ clientId, type, label, icon, cls }) => {
      const key = `${clientId}_${type}`;
      const st = notifyStatus[key];
      return (
        <button className={`notify-btn ${cls} ${st || ''}`} disabled={st === 'sending'}
          onClick={() => type === 'custom' ? setEmailModal({ clientId, name: tenants[clientId]?.name }) : sendNotification(clientId, type)}>
          {st === 'sending' ? '⏳' : st === 'sent' ? '✅' : st === 'failed' ? '❌' : icon} {st === 'sent' ? 'Sent!' : st === 'failed' ? 'Failed' : label}
        </button>
      );
    };

    return (
      <div className="dash-section">
        <div className="stats-grid">
          {[
            { icon: '💰', label: 'Total Revenue', value: formatPrice(totalRevenue), color: '#22c55e' },
            { icon: '📅', label: 'Monthly Revenue', value: formatPrice(monthlyRev), color: '#6366f1' },
            { icon: '📆', label: 'Yearly Revenue', value: formatPrice(yearlyRev), color: '#8b5cf6' },
            { icon: '✅', label: 'Paid', value: paidCount, color: '#22c55e' },
            { icon: '⏳', label: 'Pending', value: pendingCount, color: '#f59e0b' },
            { icon: '⚠️', label: 'Overdue', value: overdueCount, color: '#ef4444' },
            { icon: '🆓', label: 'Trial', value: trialCount, color: '#64748b' },
            { icon: '📋', label: 'Expiring Soon', value: expiringSoon.length, color: '#f97316' },
          ].map((s, i) => (
            <div key={i} className="stat-glass-card" style={{ '--accent': s.color }}>
              <div className="stat-card-glow" />
              <div className="stat-icon-wrap"><span className="stat-icon">{s.icon}</span></div>
              <div className="stat-info">
                <span className="stat-value">{s.value}</span>
                <span className="stat-label">{s.label}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Revenue by Plan */}
        <div className="dash-grid-2">
          <div className="glass-panel">
            <div className="panel-head"><span className="panel-icon">📊</span><h3>Revenue by Plan</h3></div>
            <div className="perf-metrics">
              {Object.entries(planDefs).map(([key, p]) => {
                const planTenants = tList.filter(([, t]) => t.plan === key);
                const planRev = planTenants.reduce((s, [, t]) => s + (t.total_revenue || 0), 0);
                return (
                  <div key={key} className="perf-row">
                    <span className="perf-label"><span className={`plan-badge ${key}`}>{p.label}</span> ({planTenants.length} clients)</span>
                    <span className="perf-value revenue-text">{formatPrice(planRev)}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="glass-panel">
            <div className="panel-head"><span className="panel-icon">⚠️</span><h3>Expiring / Expired Plans</h3></div>
            <div className="perf-metrics">
              {expired.length > 0 && expired.map(([id, t]) => (
                <div key={id} className="perf-row">
                  <span className="perf-label" style={{ color: '#fca5a5' }}>❌ {t.name} <span className="text-muted">expired {t.plan_end}</span></span>
                  <NotifyBtn clientId={id} type="plan_expired" label="Notify" icon="📧" cls="danger" />
                </div>
              ))}
              {expiringSoon.length > 0 && expiringSoon.map(([id, t]) => (
                <div key={id} className="perf-row">
                  <span className="perf-label" style={{ color: '#fcd34d' }}>⏰ {t.name} <span className="text-muted">expires {t.plan_end}</span></span>
                  <NotifyBtn clientId={id} type="plan_expiry" label="Remind" icon="📧" cls="warn" />
                </div>
              ))}
              {expired.length === 0 && expiringSoon.length === 0 && (
                <div className="perf-row"><span className="perf-label" style={{ color: 'rgba(255,255,255,.3)' }}>No expiring plans 🎉</span></div>
              )}
            </div>
          </div>
        </div>

        {/* Payment Table */}
        <div className="glass-panel">
          <div className="panel-head"><span className="panel-icon">💳</span><h3>Payment Details & Notifications</h3></div>
          <div className="table-wrap">
            <table className="dash-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Plan</th>
                  <th>Cycle</th>
                  <th>Status</th>
                  <th>Last Payment</th>
                  <th>Plan End</th>
                  <th>Revenue</th>
                  <th>Email Notifications</th>
                </tr>
              </thead>
              <tbody>
                {tList.map(([id, t]) => (
                  <tr key={id}>
                    <td>
                      <div className="tenant-cell">
                        <span className="tenant-name">{t.name}</span>
                        <span className="tenant-id">{id}</span>
                      </div>
                    </td>
                    <td><span className={`plan-badge ${t.plan || 'free'}`}>{(t.plan || 'free').toUpperCase()}</span></td>
                    <td>{t.billing_cycle === 'yearly' ? '📅 Yearly' : '📅 Monthly'}</td>
                    <td><span className={`pay-badge ${t.payment_status || 'trial'}`}>{(t.payment_status || 'trial').toUpperCase()}</span></td>
                    <td>{t.last_payment_date || '—'}</td>
                    <td>
                      <span className={t.plan_end && t.plan_end < today ? 'text-expired' : t.plan_end && t.plan_end <= soon ? 'text-expiring' : ''}>
                        {t.plan_end || '—'}
                      </span>
                    </td>
                    <td><span className="revenue-text">{formatPrice(t.total_revenue || 0)}</span></td>
                    <td>
                      <div className="notify-actions">
                        <NotifyBtn clientId={id} type="due_reminder" label="Due" icon="💸" cls="warn" />
                        <NotifyBtn clientId={id} type="plan_expiry" label="Expiry" icon="⏰" cls="info" />
                        <NotifyBtn clientId={id} type="payment_receipt" label="Receipt" icon="🧾" cls="success" />
                        <NotifyBtn clientId={id} type="custom" label="Custom" icon="✉️" cls="default" />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Custom Email Modal */}
        {emailModal && (
          <div className="modal-overlay" onClick={() => setEmailModal(null)}>
            <div className="modal-glass" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>✉️ Send Custom Email to {emailModal.name}</h3>
                <button className="modal-close" onClick={() => setEmailModal(null)}>✕</button>
              </div>
              <div className="modal-body">
                <div className="modal-field">
                  <label>Subject</label>
                  <input type="text" value={emailModal.subject || ''} onChange={e => setEmailModal(prev => ({ ...prev, subject: e.target.value }))} placeholder="Email subject..." />
                </div>
                <div className="modal-field">
                  <label>Message</label>
                  <textarea className="modal-textarea" rows={5} value={emailModal.message || ''} onChange={e => setEmailModal(prev => ({ ...prev, message: e.target.value }))} placeholder="Type your message here..." />
                </div>
              </div>
              <div className="modal-footer">
                <button className="modal-btn cancel" onClick={() => setEmailModal(null)}>Cancel</button>
                <button className="modal-btn save" onClick={() => {
                  sendNotification(emailModal.clientId, 'custom', `Subject: ${emailModal.subject || 'No Subject'}\n${emailModal.message || ''}`);
                  setEmailModal(null);
                }}>📧 Send Email</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderPlans = () => {
    const totalPlans = plans.length;
    const activePlans = plans.filter(p => p.is_active).length;
    const totalTenants = plans.reduce((s, p) => s + (p.tenant_count || 0), 0);
    const emptyPlan = { slug: '', label: '', api_limit: 100, price_monthly: 0, price_yearly: 0, max_features: 2, is_active: 1 };

    return (
      <div className="dash-section">
        <div className="stats-grid">
          {[
            { icon: '💎', label: 'Total Plans', value: totalPlans, color: '#6366f1' },
            { icon: '✅', label: 'Active Plans', value: activePlans, color: '#22c55e' },
            { icon: '🏢', label: 'Total Tenants', value: totalTenants, color: '#f59e0b' },
          ].map((s, i) => (
            <div key={i} className="stat-glass-card" style={{ '--accent': s.color }}>
              <div className="stat-card-glow" />
              <div className="stat-icon-wrap"><span className="stat-icon">{s.icon}</span></div>
              <div className="stat-info">
                <span className="stat-value">{s.value}</span>
                <span className="stat-label">{s.label}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="glass-panel">
          <div className="panel-head">
            <span className="panel-icon">💎</span>
            <h3>Plan Management</h3>
            <button className="tenant-add-btn" onClick={() => setPlanModal({ mode: 'add', id: null, data: { ...emptyPlan } })}>+ Add Plan</button>
          </div>

          <div className="dash-grid-2" style={{ padding: 20, gap: 20 }}>
            {plans.map(p => (
              <div key={p.id} className="glass-panel" style={{ padding: 24, position: 'relative', opacity: p.is_active ? 1 : 0.5 }}>
                {!p.is_active && <span className="pay-badge overdue" style={{ position: 'absolute', top: 12, right: 12 }}>INACTIVE</span>}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                  <span className={`plan-badge ${p.slug}`} style={{ fontSize: '1rem', padding: '6px 16px' }}>{p.label}</span>
                  <span className="text-muted" style={{ fontSize: '.78rem' }}>({p.slug})</span>
                  <span className="feature-chip" style={{ marginLeft: 'auto' }}>{p.tenant_count} tenant{p.tenant_count !== 1 ? 's' : ''}</span>
                </div>
                <div className="perf-metrics">
                  <div className="perf-row"><span className="perf-label">Monthly Price</span><span className="revenue-text">{formatRupee(p.price_monthly)}</span></div>
                  <div className="perf-row"><span className="perf-label">Yearly Price</span><span className="revenue-text">{formatRupee(p.price_yearly)}</span></div>
                  <div className="perf-row"><span className="perf-label">API Limit</span><span className="perf-value">{p.api_limit === -1 ? 'Unlimited' : p.api_limit.toLocaleString()}</span></div>
                  <div className="perf-row"><span className="perf-label">Max Features</span><span className="perf-value">{p.max_features}</span></div>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                  <button className="t-action-btn edit" onClick={() => setPlanModal({
                    mode: 'edit', id: p.id,
                    data: { slug: p.slug, label: p.label, api_limit: p.api_limit, price_monthly: p.price_monthly, price_yearly: p.price_yearly, max_features: p.max_features, is_active: p.is_active }
                  })}>✏️ Edit</button>
                  <button className="t-action-btn delete" onClick={() => { if (window.confirm(`Delete plan "${p.label}"?`)) deletePlan(p.id); }}>🗑️ Delete</button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Comparison Table */}
        <div className="glass-panel" style={{ marginTop: 20 }}>
          <div className="panel-head"><span className="panel-icon">📊</span><h3>Plan Comparison</h3></div>
          <div className="table-wrap">
            <table className="dash-table">
              <thead><tr><th>Plan</th><th>Monthly</th><th>Yearly</th><th>API Limit</th><th>Max Features</th><th>Tenants</th><th>Status</th></tr></thead>
              <tbody>
                {plans.map(p => (
                  <tr key={p.id}>
                    <td><span className={`plan-badge ${p.slug}`}>{p.label}</span></td>
                    <td><span className="revenue-text">{formatRupee(p.price_monthly)}</span></td>
                    <td><span className="revenue-text">{formatRupee(p.price_yearly)}</span></td>
                    <td>{p.api_limit === -1 ? '∞' : p.api_limit.toLocaleString()}</td>
                    <td>{p.max_features}</td>
                    <td><span className="feature-chip">{p.tenant_count}</span></td>
                    <td><span className={`status-dot ${p.is_active ? 'active' : 'inactive'}`}>{p.is_active ? 'Active' : 'Inactive'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Plan Modal */}
        {planModal && (
          <div className="modal-overlay" onClick={() => setPlanModal(null)}>
            <div className="modal-glass" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>{planModal.mode === 'add' ? '➕ Add New Plan' : `✏️ Edit: ${planModal.data.label}`}</h3>
                <button className="modal-close" onClick={() => setPlanModal(null)}>✕</button>
              </div>
              <div className="modal-body">
                <div className="modal-row">
                  <div className="modal-field flex1">
                    <label>Plan Label</label>
                    <input type="text" value={planModal.data.label} onChange={e => setPlanModal(p => ({...p, data: {...p.data, label: e.target.value}}))} placeholder="e.g. Professional" />
                  </div>
                  <div className="modal-field flex1">
                    <label>Slug</label>
                    <input type="text" value={planModal.data.slug} onChange={e => setPlanModal(p => ({...p, data: {...p.data, slug: e.target.value}}))} placeholder="e.g. professional" disabled={planModal.mode === 'edit'} />
                  </div>
                </div>
                <div className="modal-row">
                  <div className="modal-field flex1">
                    <label>Monthly Price (₹)</label>
                    <input type="number" value={planModal.data.price_monthly} onChange={e => setPlanModal(p => ({...p, data: {...p.data, price_monthly: parseFloat(e.target.value)||0}}))} />
                    <span className="field-hint">{formatRupee(planModal.data.price_monthly || 0)}</span>
                  </div>
                  <div className="modal-field flex1">
                    <label>Yearly Price (₹)</label>
                    <input type="number" value={planModal.data.price_yearly} onChange={e => setPlanModal(p => ({...p, data: {...p.data, price_yearly: parseFloat(e.target.value)||0}}))} />
                    <span className="field-hint">{formatRupee(planModal.data.price_yearly || 0)}</span>
                  </div>
                </div>
                <div className="modal-row">
                  <div className="modal-field flex1">
                    <label>API Limit (per month)</label>
                    <input type="number" value={planModal.data.api_limit} onChange={e => setPlanModal(p => ({...p, data: {...p.data, api_limit: parseInt(e.target.value)||0}}))} />
                    <span className="field-hint">-1 = Unlimited</span>
                  </div>
                  <div className="modal-field flex1">
                    <label>Max Features</label>
                    <input type="number" value={planModal.data.max_features} onChange={e => setPlanModal(p => ({...p, data: {...p.data, max_features: parseInt(e.target.value)||0}}))} />
                  </div>
                </div>
                <div className="modal-field">
                  <label>Status</label>
                  <div className="toggle-wrap" onClick={() => setPlanModal(p => ({...p, data: {...p.data, is_active: p.data.is_active ? 0 : 1}}))}>
                    <div className={`toggle-track ${planModal.data.is_active ? 'on' : ''}`}><div className="toggle-thumb" /></div>
                    <span>{planModal.data.is_active ? 'Active' : 'Inactive'}</span>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="modal-btn cancel" onClick={() => setPlanModal(null)}>Cancel</button>
                <button className="modal-btn save" onClick={savePlan}>{planModal.mode === 'add' ? 'Create Plan' : 'Save Changes'}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const permLabels = {
    tenants: '🏢 Tenants', payments: '💳 Payments', plans: '💎 Plans',
    data: '🗂️ Data', api: '🌐 API', documents: '📄 Documents',
    analytics: '📈 Analytics', logs: '📋 Logs', security: '🔒 Security',
    system: '⚙️ System', administration: '👥 Admin',
  };

  const renderAdministration = () => {
    const roleColors = { master: '#6366f1', supervisor: '#f59e0b', support: '#22c55e' };
    const roleIcons = { master: '👑', supervisor: '🛡️', support: '🎧' };
    const emptyAdmin = { username: '', password: '', display_name: '', email: '', phone: '', role: 'support', is_active: true, permissions: { ...defaultPerms['support'] } };

    const viewPassword = async (id) => {
      try {
        const res = await fetch(`${API_BASE_URL}/admin/admins/${id}/password`);
        const d = await res.json();
        if (d.password) setViewPw({ id, username: d.username, password: d.password });
      } catch { }
    };

    const resetPassword = async (admin) => {
      const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
      const newPw = Array.from({ length: 12 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
      if (!window.confirm(`Reset password for "${admin.display_name || admin.username}"?\n\nNew password: ${newPw}`)) return;
      try {
        await fetch(`${API_BASE_URL}/admin/admins/${admin.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password: newPw }) });
        setViewPw({ id: admin.id, username: admin.username, password: newPw });
      } catch { }
    };

    return (
      <div className="dash-section">
        <div className="stats-grid">
          {[
            { icon: '👥', label: 'Total Admins', value: admins.length, color: '#6366f1' },
            { icon: '👑', label: 'Masters', value: admins.filter(a => a.role === 'master').length, color: '#8b5cf6' },
            { icon: '🛡️', label: 'Supervisors', value: admins.filter(a => a.role === 'supervisor').length, color: '#f59e0b' },
            { icon: '🎧', label: 'Support', value: admins.filter(a => a.role === 'support').length, color: '#22c55e' },
          ].map((s, i) => (
            <div key={i} className="stat-glass-card" style={{ '--accent': s.color }}>
              <div className="stat-card-glow" />
              <div className="stat-icon-wrap"><span className="stat-icon">{s.icon}</span></div>
              <div className="stat-info">
                <span className="stat-value">{s.value}</span>
                <span className="stat-label">{s.label}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="glass-panel">
          <div className="panel-head">
            <span className="panel-icon">👥</span>
            <h3>Admin Users</h3>
            {(adminRole === 'master' || adminPerms.administration) && <button className="tenant-add-btn" onClick={() => setAdminModal({ mode: 'add', id: null, data: { ...emptyAdmin } })}>+ Add Admin</button>}
          </div>
          <div className="table-wrap">
            <table className="dash-table">
              <thead><tr><th>User</th><th>Role</th><th>Email</th><th>Phone</th><th>Last Login</th><th>Status</th>{(adminRole === 'master' || adminPerms.administration) && <th>Actions</th>}</tr></thead>
              <tbody>
                {admins.map(a => (
                  <tr key={a.id}>
                    <td>
                      <div className="tenant-cell">
                        <span className="tenant-name">{roleIcons[a.role]} {a.display_name || a.username}</span>
                        <span className="tenant-id">@{a.username}</span>
                      </div>
                    </td>
                    <td><span className="plan-badge" style={{ background: roleColors[a.role], color: '#fff' }}>{a.role.toUpperCase()}</span></td>
                    <td><span className="text-muted">{a.email || '—'}</span></td>
                    <td><span className="text-muted">{a.phone || '—'}</span></td>
                    <td><span className="text-muted">{a.last_login || 'Never'}</span></td>
                    <td><span className={`status-dot ${a.is_active ? 'active' : 'inactive'}`}>{a.is_active ? 'Active' : 'Inactive'}</span></td>
                    {(adminRole === 'master' || adminPerms.administration) && <td>
                      <div className="tenant-actions">
                        {adminRole === 'master' && <button className="t-action-btn" title="View Password" onClick={() => viewPassword(a.id)} style={{background:'rgba(99,102,241,.15)',color:'#a5b4fc'}}>👁️</button>}
                        {adminRole === 'master' && <button className="t-action-btn" title="Reset Password" onClick={() => resetPassword(a)} style={{background:'rgba(251,191,36,.15)',color:'#fbbf24'}}>🔄</button>}
                        <button className="t-action-btn edit" onClick={() => setAdminModal({ mode: 'edit', id: a.id, data: { display_name: a.display_name, email: a.email, phone: a.phone, role: a.role, is_active: a.is_active, permissions: { ...a.permissions }, password: '' } })}>✏️</button>
                        <button className="t-action-btn delete" onClick={() => { if (window.confirm(`Delete admin "${a.display_name || a.username}"?`)) deleteAdmin(a.id); }}>🗑️</button>
                      </div>
                    </td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Role Permissions Matrix */}
        <div className="glass-panel" style={{ marginTop: 20 }}>
          <div className="panel-head"><span className="panel-icon">🔐</span><h3>Role Permissions Matrix</h3></div>
          <div className="table-wrap">
            <table className="dash-table">
              <thead><tr><th>Permission</th><th>👑 Master</th><th>🛡️ Supervisor</th><th>🎧 Support</th></tr></thead>
              <tbody>
                {Object.keys(permLabels).map(k => (
                  <tr key={k}>
                    <td>{permLabels[k]}</td>
                    {['master','supervisor','support'].map(r => (
                      <td key={r} style={{ textAlign: 'center' }}>
                        <span style={{ color: defaultPerms[r]?.[k] ? '#86efac' : 'rgba(255,255,255,.2)', fontSize: '1.1rem' }}>
                          {defaultPerms[r]?.[k] ? '✅' : '❌'}
                        </span>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {viewPw && (
          <div className="modal-overlay" onClick={() => setViewPw(null)}>
            <div className="modal-glass" onClick={e => e.stopPropagation()} style={{maxWidth:420}}>
              <div className="modal-header"><h3>🔑 Password</h3><button className="modal-close" onClick={() => setViewPw(null)}>✕</button></div>
              <div className="modal-body">
                <div className="modal-field"><label>Username</label><input type="text" value={viewPw.username} readOnly style={{opacity:.7}} /></div>
                <div className="modal-field"><label>Password</label>
                  <div style={{display:'flex',gap:8}}>
                    <input type="text" value={viewPw.password} readOnly style={{fontFamily:'monospace',letterSpacing:1}} />
                    <button className="modal-btn save" style={{whiteSpace:'nowrap'}} onClick={() => {navigator.clipboard.writeText(viewPw.password)}}>📋 Copy</button>
                  </div>
                </div>
              </div>
              <div className="modal-footer"><button className="modal-btn cancel" onClick={() => setViewPw(null)}>Close</button></div>
            </div>
          </div>
        )}

        {/* Admin Modal */}
        {adminModal && (
          <div className="modal-overlay" onClick={() => setAdminModal(null)}>
            <div className="modal-glass wide" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>{adminModal.mode === 'add' ? '➕ Add New Admin' : '✏️ Edit Admin'}</h3>
                <button className="modal-close" onClick={() => setAdminModal(null)}>✕</button>
              </div>
              <div className="modal-body">
                {adminModal.mode === 'add' && (
                  <div className="modal-field">
                    <label>Username</label>
                    <input type="text" value={adminModal.data.username || ''} onChange={e => setAdminModal(p => ({...p, data: {...p.data, username: e.target.value}}))} placeholder="e.g. john_admin" />
                  </div>
                )}
                <div className="modal-row">
                  <div className="modal-field flex1">
                    <label>Display Name</label>
                    <input type="text" value={adminModal.data.display_name || ''} onChange={e => setAdminModal(p => ({...p, data: {...p.data, display_name: e.target.value}}))} placeholder="John Doe" />
                  </div>
                  <div className="modal-field flex1">
                    <label>{adminModal.mode === 'add' ? 'Password' : 'New Password (leave blank to keep)'}</label>
                    <input type="text" value={adminModal.data.password || ''} onChange={e => setAdminModal(p => ({...p, data: {...p.data, password: e.target.value}}))} placeholder={adminModal.mode === 'add' ? 'Set password' : 'Leave blank to keep current'} />
                  </div>
                </div>
                <div className="modal-row">
                  <div className="modal-field flex1">
                    <label>Email</label>
                    <input type="text" value={adminModal.data.email || ''} onChange={e => setAdminModal(p => ({...p, data: {...p.data, email: e.target.value}}))} placeholder="admin@company.com" />
                  </div>
                  <div className="modal-field flex1">
                    <label>Phone</label>
                    <input type="text" value={adminModal.data.phone || ''} onChange={e => setAdminModal(p => ({...p, data: {...p.data, phone: e.target.value}}))} placeholder="+91-9876543210" />
                  </div>
                </div>
                <div className="modal-field">
                  <label>Role</label>
                  <div className="plan-selector">
                    {['master','supervisor','support'].map(r => (
                      <div key={r} className={`plan-option ${adminModal.data.role === r ? 'selected' : ''}`} onClick={() => setAdminModal(p => ({...p, data: {...p.data, role: r, permissions: { ...(defaultPerms[r] || {}) }}})) }>
                        <span className="plan-option-name">{roleIcons[r]} {r.charAt(0).toUpperCase() + r.slice(1)}</span>
                        <span className="plan-option-price" style={{ fontSize: '.72rem', opacity: .7 }}>
                          {r === 'master' ? 'Full access' : r === 'supervisor' ? 'Most sections' : 'Limited access'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="modal-field">
                  <label>Permissions</label>
                  <div className="feature-checkboxes">
                    {Object.entries(permLabels).map(([k, label]) => (
                      <label key={k} className={`feat-check ${adminModal.data.permissions?.[k] ? 'checked' : ''}`}>
                        <input type="checkbox" checked={!!adminModal.data.permissions?.[k]} onChange={e => setAdminModal(p => ({...p, data: {...p.data, permissions: {...p.data.permissions, [k]: e.target.checked}}}))} />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="modal-field">
                  <label>Status</label>
                  <div className="toggle-wrap" onClick={() => setAdminModal(p => ({...p, data: {...p.data, is_active: !p.data.is_active}}))}>
                    <div className={`toggle-track ${adminModal.data.is_active ? 'on' : ''}`}><div className="toggle-thumb" /></div>
                    <span>{adminModal.data.is_active ? 'Active' : 'Inactive'}</span>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="modal-btn cancel" onClick={() => setAdminModal(null)}>Cancel</button>
                <button className="modal-btn save" onClick={saveAdmin}>{adminModal.mode === 'add' ? 'Create Admin' : 'Save Changes'}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const sectionMap = {
    overview: renderOverview,
    tenants: renderTenants,
    payments: renderPayments,
    data: renderData,
    api: renderAPI,
    documents: renderDocuments,
    analytics: renderAnalytics,
    logs: renderLogs,
    security: renderSecurity,
    system: renderSystem,
    plans: renderPlans,
    administration: renderAdministration,
  };

  return (
    <div className="admin-dashboard">
      <div className="dash-bg-gradient" />
      <div className="dash-bg-mesh" />

      {/* Sidebar */}
      <aside className={`dash-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-glass" />
        <div className="sidebar-content">
          <div className="sidebar-brand">
            <img src={griffsoLogo} alt="Griffso" style={{ width: '36px', height: '36px', objectFit: 'cover', borderRadius: '50%', flexShrink: 0 }} />
            {!sidebarCollapsed && <span className="brand-text">Admin Panel</span>}
          </div>

          <nav className="sidebar-nav">
            {navItems.map(item => (
              <button
                key={item.id}
                className={`nav-btn ${activeSection === item.id ? 'active' : ''}`}
                onClick={() => setActiveSection(item.id)}
                title={item.label}
              >
                <span className="nav-btn-icon">{item.icon}</span>
                {!sidebarCollapsed && <span className="nav-btn-label">{item.label}</span>}
                {activeSection === item.id && <div className="nav-active-indicator" />}
              </button>
            ))}
          </nav>

          <div className="sidebar-footer">
            <button className="nav-btn logout-btn" onClick={handleLogout}>
              <span className="nav-btn-icon">🚪</span>
              {!sidebarCollapsed && <span className="nav-btn-label">Logout</span>}
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className={`dash-main ${sidebarCollapsed ? 'expanded' : ''}`}>
        {/* Top bar */}
        <header className="dash-topbar">
          <div className="topbar-glass" />
          <div className="topbar-content">
            <div className="topbar-left">
              <button className="collapse-btn" onClick={() => setSidebarCollapsed(!sidebarCollapsed)}>
                {sidebarCollapsed ? '☰' : '✕'}
              </button>
              <div className="topbar-title">
                <h2>{navItems.find(n => n.id === activeSection)?.icon} {navItems.find(n => n.id === activeSection)?.label}</h2>
              </div>
            </div>
            <div className="topbar-right">
              <div className="topbar-time">
                <span className="time-icon">🕐</span>
                <span>{currentTime.toLocaleTimeString()}</span>
              </div>
              <button className="refresh-btn" onClick={fetchHealth} disabled={loading}>
                <span className={loading ? 'spin-icon' : ''}>🔄</span>
              </button>
              <div className="topbar-status">
                <span className={`status-indicator ${healthData?.status === 'healthy' ? 'online' : 'offline'}`} />
                <span>{healthData?.status === 'healthy' ? 'Online' : 'Offline'}</span>
              </div>
              <div className="topbar-avatar">
                <span className="avatar-icon">👤</span>
                <span className="avatar-name">{adminUser}</span>
                <span className="plan-badge" style={{ marginLeft: 6, fontSize: '.65rem', padding: '2px 8px', background: adminRole === 'master' ? '#6366f1' : adminRole === 'supervisor' ? '#f59e0b' : '#22c55e', color: '#fff' }}>{adminRole.toUpperCase()}</span>
              </div>
              <button className="topbar-logout-btn" onClick={handleLogout} title="Logout">🚪</button>
            </div>
          </div>
        </header>

        <div className="dash-body">
          {loading && !healthData ? (
            <div className="dash-loader">
              <div className="loader-ring" />
              <p>Loading system data...</p>
            </div>
          ) : (
            sectionMap[activeSection]?.()
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
